import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Form } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import CustomerDetails from "./CustomerDetails";
import InvoiceDetails from "./InvoiceDetails";
import ProductDetails from "./ProductDetails";
import ProductTable from "./ProductTable";
import PaymentDetails from "./PaymentDetails";
import useProductHandlers from "./hooks/useProductHandlers";
import useCalculations from "./hooks/useCalculations";
import "./SalesForm.css";
import baseURL from "./../../../../Url/NodeBaseURL";
import SalesFormSection from "./SalesForm3Section";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PDFLayout from "./TaxInvoiceA4";

const SalesForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [salesFormData, setSalesFormData] = useState({});
  const [salesItems, setSalesItems] = useState([]);
  const [showPDFDownload, setShowPDFDownload] = useState(false);
  const [highlightDelivered, setHighlightDelivered] = useState(false);
  const [savedNetPayableAmount, setSavedNetPayableAmount] = useState(0);
  const [isManualNetPayableSet, setIsManualNetPayableSet] = useState(false);
  const setNetPayableEditableRef = useRef(null);

  const [customers, setCustomers] = useState([]);//
  const [saveLoading, setSaveLoading] = useState(false);
  const [metal, setMetal] = useState("");
  const [
    roundOffAmountForSaveRepairsApiCall,
    setRoundOffAmountForSaveRepairsApiCall,
  ] = useState(0);

  const [oldSalesData, setOldSalesData] = useState(
    JSON.parse(localStorage.getItem("oldSalesData")) || []
  );

  // Track if page opened via edit (to avoid overriding invoice number with latest)
  const isEditingFromStateRef = useRef(!!(location.state && location.state.invoice_number));
  
  console.log("🔍 DEBUG: Initial state check", {
    hasLocationState: !!location.state,
    invoiceFromState: location.state?.invoice_number,
    isEditingFromState: isEditingFromStateRef.current
  });

  const [schemeSalesData, setSchemeSalesData] = useState(
    JSON.parse(localStorage.getItem("schemeSalesData")) || []
  );

  const [salesReturnData, setSalesReturnData] = useState([]);
  const [resetActiveForm, setResetActiveForm] = useState(false);
  const [shouldAutoEditFirstProduct, setShouldAutoEditFirstProduct] =
    useState(false);

  // 1️⃣ Try to get isEstimatePage from state (Estimates page)
  const isEstimatePageState = location.state?.isEstimatePage;

  // 2️⃣ Try to get isEstimatePage from URL query (Sales page)
  const searchParams = new URLSearchParams(location.search);
  const isEstimatePageQuery = searchParams.get("isEstimatePage");

  // 3️⃣ Determine final isEstimatePage
  const isEstimatePage =
    isEstimatePageState !== undefined
      ? isEstimatePageState
      : isEstimatePageQuery === "true";

  useEffect(() => {});

  // Auto-select estimate when coming from estimate page
  useEffect(() => {
    if (location.state?.fromEstimate && location.state?.estimateNumber) {
      const estimateNumber = location.state.estimateNumber;
      console.log("Auto-selecting estimate:", estimateNumber);

      // Set the estimate number in the form
      setFormData((prev) => ({
        ...prev,
        estimate_number: estimateNumber,
      }));

      // Trigger the estimate fetch
      if (estimateNumber) {
        fetchEstimateDetails(estimateNumber);
      }
    }
  }, [location.state]);
  const invoiceNumberFromState = location.state?.invoice_number;
  useEffect(() => {
    if (invoiceNumberFromState) {
      console.log("🔍 DEBUG: Loading invoice from state", {
        invoiceNumberFromState,
        currentFormDataInvoice: formData.invoice_number
      });
      loadInvoiceData(invoiceNumberFromState);
    }
  }, [invoiceNumberFromState]);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem("oldSalesData", JSON.stringify(oldSalesData));
  }, [oldSalesData]);

  useEffect(() => {
    localStorage.setItem("schemeSalesData", JSON.stringify(schemeSalesData));
  }, [schemeSalesData]);

  const {
    formData,
    setFormData,
    products,
    data,
    isQtyEditable,
    handleChange,
    handleBarcodeChange,
    handleProductChange,
    handleProductNameChange,
    handleMetalTypeChange,
    handleDesignNameChange,
    filteredDesignOptions,
    filteredPurityOptions,
    filteredMetalTypes,
    uniqueProducts,
    metaltypeOptions,
    purityOptions,
    categoryOptions,
    subcategoryOptions,
    designOptions,
    isBarcodeSelected,
    handleImageChange,
    image,
    fileInputRef,
    clearImage,
    captureImage,
    setShowWebcam,
    showWebcam,
    webcamRef,
    setShowOptions,
    showOptions,
    fetchCategory,
    fetchSubCategory,
    isManualTotalPriceChange,
    setIsManualTotalPriceChange,
    isTotalPriceCleared,
    setIsTotalPriceCleared,
    manualTotalPriceRef,
    updateLocalStock,
    restoreLocalStock,
    clearLocalStock,
    localStock,
  } = useProductHandlers();

  // Load data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(`salesReturnData`);
    if (savedData) {
      setSalesReturnData(JSON.parse(savedData));
    }
  }, []);

  // useEffect(() => {
  //   if (tabId && !hasLoadedRepairDetails) {
  //     const savedData = localStorage.getItem(`repairDetails`);
  //     if (savedData) {
  //       setRepairDetails(JSON.parse(savedData));
  //     }
  //      setHasLoadedRepairDetails(true);
  //   }
  // }, [tabId, hasLoadedRepairDetails]);
  // useEffect(() => {
  //   if (tabId) {
  //     localStorage.setItem(
  //       `repairDetails`,
  //       JSON.stringify(repairDetails)
  //     );
  //   }
  // }, [repairDetails, tabId]);

  useEffect(() => {
    const savedData = localStorage.getItem(`oldTableData`);
    if (savedData) {
      setOldTableData(JSON.parse(savedData));
    }
  }, []);

  useEffect(() => {
    const savedData = localStorage.getItem(`schemeTableData`);
    if (savedData) {
      setSchemeTableData(JSON.parse(savedData));
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldReset = params.get("reset") === "true";

    // Only load from localStorage if not resetting
    if (!shouldReset) {
      const savedDiscount = localStorage.getItem(`discount`);
      if (savedDiscount) {
        setDiscount(parseFloat(savedDiscount) || "");
      }
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldReset = params.get("reset") === "true";

    // Only load from localStorage if not resetting
    if (!shouldReset) {
      const savedPaymentDetails = localStorage.getItem(
        `paymentDetails`
      );
      if (savedPaymentDetails) {
        setPaymentDetails(JSON.parse(savedPaymentDetails));
      }
    }
  }, [location.search]);

  const [repairDetails, setRepairDetails] = useState([]);

  // Auto-edit first product when estimate is loaded
  useEffect(() => {
    if (
      shouldAutoEditFirstProduct &&
      repairDetails &&
      repairDetails.length > 0
    ) {
      console.log("Auto-editing first product from estimate");
      handleEdit(0);
      setShouldAutoEditFirstProduct(false); // Reset the flag
    }
  }, [repairDetails, shouldAutoEditFirstProduct]);

  useEffect(() => {
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(repairDetails)
    );
  }, [repairDetails]);

  // Removed URL parameter reset functionality to prevent unexpected form clearing
  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   const shouldReset = params.get("reset") === "true";

  //   if (shouldReset) {
  //     // Clear relevant localStorage keys
  //     localStorage.removeItem(`repairDetails`);
  //     localStorage.removeItem(`paymentDetails`);
  //     localStorage.removeItem(`discount`);
  //     localStorage.removeItem(`oldTableData`);
  //     localStorage.removeItem(`schemeTableData`);
  //     localStorage.removeItem(`salesReturnData`);
  //     localStorage.removeItem(`activeForm`);
  //     localStorage.removeItem("oldSalesData");
  //     localStorage.removeItem("schemeSalesData");

  //     // Reset state
  //     setRepairDetails([]);
  //     setSavedNetPayableAmount(0);
  //     setIsManualNetPayableSet(false);
  //     setManualNetAmount(0);
  //     setManualNetPayAmount(0);
  //     setDiscount(0);
  //     setOldTableData([]);
  //     setSchemeTableData([]);
  //     setOldSalesData([]);
  //     setSchemeSalesData([]);
  //     setSalesReturnData([]);
  //     setSelectedRows([]);
  //     setIsAllSelected(false);
  //     setReturnData({
  //       invoice_number: "",
  //     });

  //     // Trigger reset of active form
  //     setResetActiveForm(true);

  //     setFormData({
  //       customer_id: "",
  //       mobile: "",
  //       account_name: "",
  //       email: "",
  //       address1: "",
  //       address2: "",
  //       city: "",
  //       pincode: "",
  //       state: "",
  //       state_code: "",
  //       aadhar_card: "",
  //       gst_in: "",
  //       pan_card: "",
  //       date: new Date().toISOString().split("T")[0],
  //       invoice_number: "",
  //     });
  //   }
  // }, [location.search]);

  // Reset the resetActiveForm flag after a short delay
  useEffect(() => {
    if (resetActiveForm) {
      const timer = setTimeout(() => {
        setResetActiveForm(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [resetActiveForm]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${baseURL}/get/account-details`);
        if (!response.ok) {
          throw new Error("Failed to fetch customers");
        }
        const result = await response.json();
        const filteredCustomers = result.filter(
          (item) =>
            item.account_group === "CUSTOMERS" ||
            item.account_group === "SUPPLIERS"
        );
        setCustomers(filteredCustomers);
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch last invoice number
  useEffect(() => {
    const fetchLastInvoiceNumber = async () => {
      try {
        const response = await axios.get(`${baseURL}/lastInvoiceNumber`);
        const latestInvoiceNumber = response.data.lastInvoiceNumber;
        
        console.log("🔍 DEBUG: Fetching last invoice number", {
          latestInvoiceNumber,
          isEditingFromState: isEditingFromStateRef.current,
          currentFormDataInvoice: formData.invoice_number
        });
        
        // Do not override if we came from edit, or if an invoice number is already set
        setFormData((prev) => {
          if (isEditingFromStateRef.current || (prev.invoice_number && prev.invoice_number !== "")) {
            console.log("🔍 DEBUG: NOT overriding invoice number", {
              reason: isEditingFromStateRef.current ? "editing from state" : "invoice already set",
              currentInvoice: prev.invoice_number,
              wouldSetTo: latestInvoiceNumber
            });
            return prev;
          }
          
          console.log("🔍 DEBUG: Setting invoice number to latest", {
            from: prev.invoice_number,
            to: latestInvoiceNumber
          });
          
          return {
            ...prev,
            invoice_number: latestInvoiceNumber,
          };
        });
      } catch (error) {
        console.error("Error fetching invoice number:", error);
      }
    };

    fetchLastInvoiceNumber();
  }, []);

  const [selectedMobile, setSelectedMobile] = useState("");
  const [uniqueInvoice, setUniqueInvoice] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [sortedInvoices, setSortedInvoices] = useState([]);
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(-1);

  const [returnData, setReturnData] = useState({
    invoice_number: "",
  });

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await axios.get(`${baseURL}/get/repair-details`);
        const allRepairs = response.data;

        // Group by invoice_number
        const grouped = {};
        for (const entry of allRepairs) {
          if (!grouped[entry.invoice_number]) {
            grouped[entry.invoice_number] = [];
          }
          grouped[entry.invoice_number].push(entry);
        }

        // Convert to array of { invoice_number, date, entries: [...] }
        const groupedInvoices = Object.keys(grouped).map((invoiceNum) => {
          return {
            invoice_number: invoiceNum,
            date: grouped[invoiceNum][0].date,
            entries: grouped[invoiceNum],
          };
        });

        // Sort by date descending + invoice number descending
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const sorted = groupedInvoices.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB - dateA;
          }
          const numA = parseInt(a.invoice_number.replace(/\D/g, "")) || 0;
          const numB = parseInt(b.invoice_number.replace(/\D/g, "")) || 0;
          return numB - numA;
        });

        setSortedInvoices(sorted);
      } catch (err) {
        console.error("Failed to fetch invoices:", err);
      }
    };

    fetchInvoices();
  }, []);

  const [EstimateNo, setEstimateNo]= useState(false);

  //loading invoice data
  const loadInvoiceData = async (invoiceNumber) => {
    try {
      const response = await axios.get(
        `${baseURL}/get-repair-details/${invoiceNumber}`
      );
      const { uniqueData, repeatedData, saleReturnItems } = response.data;

      if (!uniqueData) return;

      // Find the index of the current invoice in sortedInvoices array
      const invoiceIndex = sortedInvoices.findIndex(
        (invoice) => invoice.invoice_number === invoiceNumber
      );
      if (invoiceIndex !== -1) {
        setCurrentInvoiceIndex(invoiceIndex);
      }

      // ✅ Update formData with invoice and customer details
      console.log("🔍 DEBUG: Setting formData in loadInvoiceData", {
        invoiceNumber,
        uniqueDataInvoiceNumber: uniqueData.invoice_number,
        currentFormDataInvoice: formData.invoice_number
      });
      
      setFormData((prev) => ({
        ...prev,
        invoice_number: uniqueData.invoice_number || "",
        date: uniqueData.date
          ? new Date(uniqueData.date).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
          : "",
        customer_id: uniqueData.customer_id || "",
        account_name: uniqueData.account_name || "",
        mobile: uniqueData.mobile || "",
        email: uniqueData.email || "",
        address1: uniqueData.address1 || "",
        address2: uniqueData.address2 || "",
        city: uniqueData.city || "",
        pincode: uniqueData.pincode || "",
        state: uniqueData.state || "",
        state_code: uniqueData.state_code || "",
        aadhar_card: uniqueData.aadhar_card || "",
        gst_in: uniqueData.gst_in || "",
        pan_card: uniqueData.pan_card || "",
        terms: uniqueData.terms || "",
        cash_amt: parseFloat(uniqueData.cash_amount || 0),
        card_amt: parseFloat(uniqueData.card_amt || 0),
        online_amt: parseFloat(uniqueData.online_amt || 0),
        cheque_amt: parseFloat(uniqueData.chq_amt || 0),
        net_amt: parseFloat(uniqueData.net_amount || 0),
        tax_amt: parseFloat(uniqueData.tax_amount || 0),
        total_amt: parseFloat(uniqueData.taxable_amount || 0),
        round_off: 0, // add if available
      }));

      // ✅ Update repairDetails (products)

      setRepairDetails(repeatedData || []);
      localStorage.setItem(
        `repairDetails`,
        JSON.stringify(repeatedData || [])
      );
      if (repeatedData?.[0]?.estimate_number != null) {
        setEstimateNo(true);
        setSelectedEstimate(repeatedData[0].estimate_number);
      } else {
        setEstimateNo(false);
      }
      localStorage.removeItem("paymentDetails");
      
      const paymentData = {
        cash_amount: Number(repeatedData?.[0]?.cash_amount || 0),
        card_amt: Number(repeatedData?.[0]?.card_amt || 0),
        chq: "",
        chq_amt: Number(repeatedData?.[0]?.chq_amt || 0),
        online: "",
        online_amt: Number(repeatedData?.[0]?.online_amt || 0),
      }; 
      setPaymentDetails(paymentData);


      // ✅ Update sales return data if available
      if (saleReturnItems && saleReturnItems.length > 0) {
        setSalesReturnData(saleReturnItems);

        // Update return data with the first invoice number from sales return items
        const firstReturnItem = saleReturnItems[0];

        if (firstReturnItem.invoice_number) {
          setReturnData((prev) => {
            const newReturnData = {
              ...prev,
              invoice_number: firstReturnItem.invoice_number,
            };

            return newReturnData;
          });
        }
      } else {
        setSalesReturnData([]);
        setReturnData({
          invoice_number: "",
        });
      }

      // Reset manual net payable flag for new invoice data
      setIsManualNetPayableSet(false);

      // Clear selected rows to reset sales return calculations
      setSelectedRows([]);

      // Mark that invoice data has been loaded
      setInvoiceDataLoaded(true);

      // Clear the navigation state if this was loaded from location.state
      if (location.state?.invoice_number === invoiceNumber) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  //navigate to different invoices
  const navigateToPreviousInvoice = async () => {
    if (currentInvoiceIndex > 0) {
      const prevInvoice = sortedInvoices[currentInvoiceIndex - 1];
      await loadInvoiceData(prevInvoice.invoice_number);
    }
  };

  const navigateToNextInvoice = async () => {
    if (currentInvoiceIndex < sortedInvoices.length - 1) {
      const nextInvoice = sortedInvoices[currentInvoiceIndex + 1];
      await loadInvoiceData(nextInvoice.invoice_number);
    }
  };

  const navigateToFirstInvoice = async () => {
    if (sortedInvoices.length > 0) {
      const firstInvoice = sortedInvoices[0];
      await loadInvoiceData(firstInvoice.invoice_number);
    }
  };

  const navigateToLastInvoice = async () => {
    if (sortedInvoices.length > 0) {
      const lastIndex = sortedInvoices.length - 1;
      const lastInvoice = sortedInvoices[lastIndex];
      await loadInvoiceData(lastInvoice.invoice_number);
    }
  };

  useEffect(() => {
    // Set the default date value to the current date in dd-mm-yyyy format if not already set
    if (!returnData.date) {
      const currentDate = new Date();
      setReturnData({
        ...returnData,
        date: formatDate(currentDate),
      });
    }
  }, [returnData, setReturnData]);

  // Utility function to format date as dd-mm-yyyy
  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Handle invoice number change
  const handleInvoiceChange = (e) => {
    const selectedInvoiceNumber = e.target.value;
    const selectedInvoice = filteredInvoices.find(
      (invoice) => invoice.invoice_number === selectedInvoiceNumber
    );

    if (selectedInvoice) {
      setReturnData({
        ...returnData,
        invoice_number: selectedInvoiceNumber,
        date: selectedInvoice.date ? formatDate(selectedInvoice.date) : "", // Format date
        terms: selectedInvoice.terms || "", // Set the terms from the selected invoice
      });
    } else {
      setReturnData({
        ...returnData,
        invoice_number: selectedInvoiceNumber,
        date: "",
        terms: "",
      });
    }
  };

  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/get-unique-repair-details`
        );
        const filteredData = response.data.filter(
          (item) => item.transaction_status === "Sales"
        );
        setUniqueInvoice(filteredData);
        setFilteredInvoices(filteredData); // Initially, show all invoices
      } catch (error) {
        console.error("Error fetching repair details:", error);
      }
    };

    fetchSales();
  }, []);

  useEffect(() => {
    // Only proceed if we have an invoice number
    if (!returnData.invoice_number) {
      return;
    }

    const fetchInvoiceDetails = async () => {
      // Check if we already have data for this invoice number
      if (
        invoiceDetails.length > 0 &&
        invoiceDetails[0]?.invoice_number === returnData.invoice_number
      ) {
        return;
      }

      if (!returnData.invoice_number) {
        setInvoiceDetails([]); // Ensure it's an empty array, not null
        setSelectedRows([]);
        setIsAllSelected(false);
        return;
      }

      try {
        const response = await axios.get(
          `${baseURL}/getsales/${returnData.invoice_number}`
        );

        // For sales returns, we want to show the original sales records
        // even if they have status "Sale Returned", because we might want to return more items
        const filteredData = response.data.filter((invoice) => {
          // Show all records for sales return context
          // This allows us to see what was originally sold and what was returned
          return true;
        });

        setInvoiceDetails(filteredData || []); // Ensure it's always an array

        // Check if there are any sales return items for this invoice
        if (salesReturnData && salesReturnData.length > 0) {
          const returnItemsForThisInvoice = salesReturnData.filter(
            (item) => item.original_invoice_number === returnData.invoice_number
          );

          if (returnItemsForThisInvoice.length > 0) {
            // Find which items from this invoice are in the sales return data
            const returnedItemCodes = returnItemsForThisInvoice.map(
              (item) => item.code
            );

            // Set selected rows based on returned items
            const selectedRowIndices = filteredData
              .map((item, index) =>
                returnedItemCodes.includes(item.code) ? index : -1
              )
              .filter((index) => index !== -1);

            setSelectedRows(selectedRowIndices);
            setIsAllSelected(
              selectedRowIndices.length === filteredData.length &&
                filteredData.length > 0
            );
          } else {
            setSelectedRows([]);
            setIsAllSelected(false);
          }
        }
      } catch (error) {
        console.error(
          `Error fetching details for invoice ${returnData.invoice_number}:`,
          error
        );
        setInvoiceDetails([]); // Set empty array on error
      }
    };

    fetchInvoiceDetails();
  }, [returnData.invoice_number, returnData]);

  // Debug useEffect to track returnData.invoice_number changes
  useEffect(() => {}, [returnData.invoice_number]);

  // Debug useEffect to track initial state
  useEffect(() => {}, []);

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(
      (cust) => String(cust.account_id) === String(customerId)
    );

    if (customer) {
      setFormData((prevData) => ({
        ...prevData,
        customer_id: customerId,
        account_name: customer.account_name || "",
        mobile: customer.mobile || "",
        email: customer.email || "",
        address1: customer.address1 || "",
        address2: customer.address2 || "",
        city: customer.city || "",
        pincode: customer.pincode || "",
        state: customer.state || "",
        state_code: customer.state_code || "",
        aadhar_card: customer.aadhar_card || "",
        gst_in: customer.gst_in || "",
        pan_card: customer.pan_card || "",
        // keep invoice_number untouched
        invoice_number: prevData.invoice_number || "",
      }));
      setSelectedMobile(customer.mobile || "");

      const filtered = uniqueInvoice.filter(
        (invoice) =>
          invoice.customer_name === customer.account_name ||
          invoice.mobile === customer.mobile
      );
      setFilteredInvoices(filtered);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        customer_id: "",
        account_name: "",
        mobile: "",
        email: "",
        address1: "",
        address2: "",
        city: "",
        pincode: "",
        state: "",
        state_code: "",
        aadhar_card: "",
        gst_in: "",
        pan_card: "",
        // preserve invoice_number
        invoice_number: prevData.invoice_number || "",
      }));
      setSelectedMobile("");
      setFilteredInvoices(uniqueInvoice);
    }
  };

  const [editIndex, setEditIndex] = useState(null);
  const [discount, setDiscount] = useState("");
  const [isManualNetMode, setIsManualNetMode] = useState(false);

  useEffect(() => {
    localStorage.setItem(`discount`, discount); // Save to localStorage when discount changes
  }, [discount]);

  const applyDiscountToRepairDetails = (discountValue) => {
    const storedRepairDetails =
      JSON.parse(localStorage.getItem(`repairDetails`)) || [];

    const updatedRepairDetails = storedRepairDetails.map((item) => {
      const makingCharges = parseFloat(item.making_charges) || 0;
      const pieceCost = parseFloat(item.pieace_cost) || 0;
      const qty = parseFloat(item.qty) || 1;
      const taxPercent = parseFloat(item.tax_percent) || 1;
      const piece_taxable_amt = parseFloat(item.piece_taxable_amt) || 0;
      const rateAmt = parseFloat(item.rate_amt) || 0;
      const stonePrice = parseFloat(item.stone_price) || 0;
      const discountAmt = parseFloat(item.disscount) || 0;
      const hmCharges = parseFloat(item.hm_charges) || 0;
      const pricingType = item.pricing;
      const festivalDiscount = parseFloat(item.festival_discount) || 0;

      let calculatedDiscount = 0;

      if (pricingType === "By fixed") {
        const pieceTaxableAmt = pieceCost * qty;
        calculatedDiscount = (pieceTaxableAmt * discountValue) / 100;

        const originalPieceTaxableAmt = item.original_piece_taxable_amt
          ? parseFloat(item.original_piece_taxable_amt)
          : pieceTaxableAmt;

        const updatedPieceTaxableAmt =
          originalPieceTaxableAmt - calculatedDiscount - festivalDiscount;
        const taxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
        const totalPrice = updatedPieceTaxableAmt + taxAmt;

        return {
          ...item,
          original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2),
          disscount: calculatedDiscount.toFixed(2),
          disscount_percentage: discountValue,
          piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: totalPrice.toFixed(2),
        };
      } else {
        calculatedDiscount = (makingCharges * discountValue) / 100;

        const previousTotalPrice = parseFloat(item.total_price) || 0;
        const originalTotalPrice = item.original_total_price
          ? parseFloat(item.original_total_price)
          : previousTotalPrice;

        const totalBeforeTax =
          rateAmt +
          stonePrice +
          makingCharges +
          hmCharges -
          calculatedDiscount -
          festivalDiscount;
        const taxAmt = (totalBeforeTax * taxPercent) / 100;
        const updatedTotalPrice = totalBeforeTax + taxAmt;

        return {
          ...item,
          original_total_price: originalTotalPrice.toFixed(2),
          disscount: calculatedDiscount.toFixed(2),
          disscount_percentage: discountValue,
          tax_amt: taxAmt.toFixed(2),
          total_price: updatedTotalPrice.toFixed(2),
        };
      }
    });

    setRepairDetails(updatedRepairDetails);
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(updatedRepairDetails)
    );
  };

  const applyDiscountAmountToRepairDetails = async (discountAmount) => {
    // get the total making charge
    const totalMakingCharges = repairDetails.reduce(
      (sum, item) => sum + parseFloat(item.making_charges) || 0,
      0
    );

    // check what will the % of discount amount on total making charges
    const discountPercentage = (discountAmount / totalMakingCharges) * 100;

    // set the discount to the discount percentage
    setDiscount(discountPercentage);
    applyDiscountToRepairDetails(discountPercentage);
  };

  const handleDiscountChange = (e) => {
    const discountValue = parseFloat(e.target.value) || "";

    setDiscount(discountValue);
    setManualNetAmount(0); // Reset manualNetAmount to exit manual mode
    setIsManualNetMode(false); // Ensure we're in normal discount mode
    applyDiscountToRepairDetails(discountValue); // Recalculate with new discount
  };

  const [festivalShowModal, festivalSetShowModal] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleFestivalShowModal = () => festivalSetShowModal(true);
  const handleFestivalCloseModal = () => festivalSetShowModal(false);
  const [appliedOffers, setAppliedOffers] = useState([]);
  const [appliedOffersFromAPI, setAppliedOffersFromAPI] = useState([]);
  const [invoiceDataLoaded, setInvoiceDataLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/offers`);
        const allOffers = response.data;

        const today = new Date();

        // Filter offers that are currently valid (within date range)
        const validOffers = allOffers.filter((offer) => {
          const validFrom = new Date(offer.valid_from);
          const validTo = new Date(offer.valid_to);
          const isDateValid = today >= validFrom && today <= validTo;
          const isNotUnapplied =
            !offer.offer_status ||
            offer.offer_status.toLowerCase() !== "unapplied";
          return isDateValid && isNotUnapplied;
        });
        // Separate applied and unapplied offers
        const appliedOffersAPI = validOffers.filter(
          (offer) =>
            offer.offer_status && offer.offer_status.toLowerCase() === "applied"
        );
        const availableOffers = validOffers.filter(
          (offer) =>
            !offer.offer_status ||
            offer.offer_status.toLowerCase() === "available" ||
            offer.offer_status === ""
        );
        // const unappliedOffers = validOffers.filter(
        //   (offer) =>
        //     !offer.offer_status ||
        //     offer.offer_status.toLowerCase() === "unapplied"
        // );

        setOffers(availableOffers);
        setAppliedOffersFromAPI(appliedOffersAPI);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching offers:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-apply offers when both appliedOffersFromAPI and repairDetails are ready
  useEffect(() => {
    if (appliedOffersFromAPI.length > 0 && repairDetails.length > 0) {
      // Clear previously applied offers when navigating to a new invoice
      if (invoiceDataLoaded) {
        setAppliedOffers([]);
        setInvoiceDataLoaded(false);
      }

      appliedOffersFromAPI.forEach((offer, index) => {
        // Prevent re-applying the same offer
        const alreadyApplied = appliedOffers.some(
          (applied) =>
            (applied.offer_id || applied._id) === (offer.offer_id || offer._id)
        );
        if (!alreadyApplied) {
          handleApply(offer, index);
        }
      });
    }
  }, [appliedOffersFromAPI, repairDetails, invoiceDataLoaded]);

  const [isAnyOfferApplied, setIsAnyOfferApplied] = useState(false);

  const handleApply = (selectedOffer, offerIndex) => {
    const storedRepairDetails =
      JSON.parse(localStorage.getItem(`repairDetails`)) || [];
    const currentOfferKey = `${
      selectedOffer.offer_id || selectedOffer._id
    }_${offerIndex}`;

    // Use current state if localStorage is empty but state has data
    let updatedRepairDetails =
      storedRepairDetails.length > 0
        ? [...storedRepairDetails]
        : [...repairDetails];

    // If no items to apply offer to, just mark the offer as applied
    if (updatedRepairDetails.length === 0) {
      setAppliedOffers((prev) => [...prev, selectedOffer]);
      setIsAnyOfferApplied(true);
      return;
    }

    updatedRepairDetails = updatedRepairDetails.map((item) => {
      const taxPercent = parseFloat(item.tax_percent) || 1;
      const pieceCost = parseFloat(item.pieace_cost) || 0;
      const qty = parseFloat(item.qty) || 1;
      const discountAmt = parseFloat(item.disscount) || 0;
      const pricingType = item.pricing;
      const rateAmt = parseFloat(item.rate_amt) || 0;
      const stonePrice = parseFloat(item.stone_price) || 0;
      const makingCharges = parseFloat(item.making_charges) || 0;
      const hmCharges = parseFloat(item.hm_charges) || 0;
      const grossWeight = parseFloat(item.gross_weight) || 0;

      const percentageDiscount =
        parseFloat(selectedOffer.discount_percentage) || 0;
      const rateDiscount = parseFloat(selectedOffer.discount_on_rate) || 0;
      const fixedPercentageDiscount =
        parseFloat(selectedOffer.discount_percent_fixed) || 0;
      const weightBasedDiscount = (rateDiscount / 10) * grossWeight;
      const totalDiscountValue =
        pricingType === "By fixed"
          ? fixedPercentageDiscount
          : percentageDiscount + weightBasedDiscount;

      if (pricingType === "By fixed") {
        const pieceTaxableAmt = pieceCost * qty;
        const originalPieceTaxableAmt = item.original_piece_taxable_amt
          ? parseFloat(item.original_piece_taxable_amt)
          : pieceTaxableAmt;

        const calculatedDiscount = (pieceTaxableAmt * totalDiscountValue) / 100;
        const updatedPieceTaxableAmt =
          originalPieceTaxableAmt - calculatedDiscount - discountAmt;
        const taxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
        const totalPrice = updatedPieceTaxableAmt + taxAmt;

        return {
          ...item,
          original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2),
          festival_discount: calculatedDiscount.toFixed(2),
          festival_discount_percentage: percentageDiscount.toFixed(2),
          festival_discount_on_rate: rateDiscount.toFixed(2),
          piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: totalPrice.toFixed(2),
        };
      } else {
        const calculatedDiscount =
          (makingCharges * percentageDiscount) / 100 +
          (rateDiscount / 10) * grossWeight;
        const totalBeforeTax =
          rateAmt +
          stonePrice +
          makingCharges +
          hmCharges -
          calculatedDiscount -
          discountAmt;

        const taxAmt = (totalBeforeTax * taxPercent) / 100;
        const updatedTotalPrice = totalBeforeTax + taxAmt;

        return {
          ...item,
          original_total_price: item.original_total_price
            ? item.original_total_price
            : parseFloat(item.total_price).toFixed(2),
          festival_discount: calculatedDiscount.toFixed(2),
          festival_discount_percentage: percentageDiscount.toFixed(2),
          festival_discount_on_rate: rateDiscount.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: updatedTotalPrice.toFixed(2),
        };
      }
    });

    setRepairDetails(updatedRepairDetails);
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(updatedRepairDetails)
    );

    // Mark the offer as applied
    setAppliedOffers((prev) => [...prev, selectedOffer]);
    setIsAnyOfferApplied(true);
  };

  const [estimate, setEstimate] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState("");
  const [estimateDetails, setEstimateDetails] = useState(null);
  const [stock, setStock] = useState(null);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const response = await axios.get(`${baseURL}/Estimate/fetchall`);

        const nowInIST = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
        const todayIST = new Date(nowInIST);
        const formattedTodayIST = todayIST.toISOString().split("T")[0]; // "YYYY-MM-DD"

        const estimatesWithoutInvoice = (response.data || []).filter((est) => {
          if (!est.date) return false;

          // Convert est.date (UTC) to IST
          const estDateIST = new Date(new Date(est.date).toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
          const formattedEstDateIST = estDateIST.toISOString().split("T")[0];

          return (
            (!est.invoice_number || est.invoice_number === "") &&
            formattedEstDateIST === formattedTodayIST
          );
        });

        setEstimate(estimatesWithoutInvoice);
      } catch (error) {
        console.error("Error fetching estimate details:", error);
      }
    };
    fetchEstimate();
  }, []);

  useEffect(() => {
    fetch(`${baseURL}/get/opening-tags-entry`) // Correct URL
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch stock entries");
        }
        return response.json();
      })
      .then((data) => {
        setStock(data.result); // Use data.result since the backend sends { result: [...] }
      })
      .catch((error) => {
        console.error("Error fetching stock entries:", error);
      });
  }, []);

  const formatDateForInput = (isoString) => {
    return new Date(isoString).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  };

  /* const fetchEstimateDetails = async (estimate_number) => {
    if (!estimate_number) return;

    try {
      const response = await axios.get(
        `${baseURL}/get-estimates/${estimate_number}`
      );

      // First, update the state with the full estimate details
      setEstimateDetails(response.data);

      if (!stock) {
        console.warn("Stock data not yet available!");
        return;
      }

      // Filter only matching repeatedData items
      const filteredData = response.data.repeatedData
        .filter((item) =>
          stock.some(
            (stockItem) =>
              stockItem.PCode_BarCode === item.code &&
              stockItem.Status === "Available"
          )
        )
        .map((item) => ({
          ...item,
          invoice_number: formData.invoice_number, // Add invoice_number from formData
          transaction_status: "Sales", // Add transaction_status as "Sales"
          date: formData.date,
        }));

      if (filteredData.length > 0) {
        // Store filtered data in localStorage
        localStorage.setItem(
          `repairDetails`,
          JSON.stringify(filteredData)
        );

        // Update state with filtered data
        setRepairDetails(filteredData);

        // Immediately retrieve and log stored data
        const storedData = JSON.parse(
          localStorage.getItem(`repairDetails`)
        );

      } else {
        // Clear localStorage if no matching data
        localStorage.removeItem(`repairDetails`);
        setRepairDetails([]); // Clear state
      }
    } catch (error) {
      console.error("Error fetching selected estimate details:", error);
    }
  }; */
  const fetchEstimateDetails = async (estimate_number) => {
    if (!estimate_number) return;

    try {
      const res = await axios.get(
        `${baseURL}/Estimate/full-details/${estimate_number}`
      );

      const data = res.data;
      const estimate = data.estimate || {};
      const products = data.products || [];
      const account = data.account || {};

      // Check if customer data is embedded in the estimate
      if (estimate.customer_id || estimate.account_name || estimate.mobile) {
      }

      // Update form data with customer details and estimate information
      const updatedFormData = {
        ...formData,
        estimate_number: estimate.estimate_number || "",
        date: estimate.date
          ? formatDateForInput(estimate.date)
          : new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
        // Customer details - try account first, then estimate
        customer_id: account.account_id || estimate.customer_id || "",
        account_name: account.account_name || estimate.account_name || "",
        mobile: account.phone || account.mobile || estimate.mobile || "",
        email: account.email || estimate.email || "",
        address1: account.address1 || estimate.address1 || "",
        address2: account.address2 || estimate.address2 || "",
        city: account.city || estimate.city || "",
        pincode: account.pincode || estimate.pincode || "",
        state: account.state || estimate.state || "",
        state_code: account.state_code || estimate.state_code || "",
        aadhar_card: account.aadhar_card || estimate.aadhar_card || "",
        gst_in: account.gst_in || estimate.gst_in || "",
        pan_card: account.pan_card || estimate.pan_card || "",
        // Payment details from estimate
        cash_amt: Number(estimate.cash_amt || 0),
        card_amt: Number(estimate.card_amt || 0),
        online_amt: Number(estimate.online_amt || 0),
        cheque_amt: Number(estimate.cheque_amt || 0),
        net_amt: Number(estimate.net_amt || 0),
        tax_amt: Number(estimate.tax_amt || 0),
        total_amt: Number(estimate.total_amt || 0),
        discount_amt: Number(estimate.discount_amt || 0),
        net_payable_amt: Number(estimate.net_payable_amt || 0),
        round_off: Number(estimate.round_off || 0),
      };

      // If customer data is missing, try to fetch it separately
      if (!updatedFormData.account_name && !updatedFormData.mobile) {
        try {
          // Try to get customer data from the estimate products
          if (products && products.length > 0) {
            const firstProduct = products[0];
            if (
              firstProduct.customer_id ||
              firstProduct.account_name ||
              firstProduct.mobile
            ) {
              const customerData = {
                ...updatedFormData,
                customer_id:
                  firstProduct.customer_id || updatedFormData.customer_id,
                account_name:
                  firstProduct.account_name || updatedFormData.account_name,
                mobile: firstProduct.mobile || updatedFormData.mobile,
                email: firstProduct.email || updatedFormData.email,
                address1: firstProduct.address1 || updatedFormData.address1,
                address2: firstProduct.address2 || updatedFormData.address2,
                city: firstProduct.city || updatedFormData.city,
                pincode: firstProduct.pincode || updatedFormData.pincode,
                state: firstProduct.state || updatedFormData.state,
                state_code:
                  firstProduct.state_code || updatedFormData.state_code,
                aadhar_card:
                  firstProduct.aadhar_card || updatedFormData.aadhar_card,
                gst_in: firstProduct.gst_in || updatedFormData.gst_in,
                pan_card: firstProduct.pan_card || updatedFormData.pan_card,
              };
              setFormData(customerData);
            }
          }
        } catch (error) {
          console.error("Error fetching customer data:", error);
        }
      } else {
        // Update the form data state
        setFormData(updatedFormData);
      }

      // Update payment details from estimate
      const paymentData = {
        cash_amount: Number(estimate.cash_amt || 0),
        card_amt: Number(estimate.card_amt || 0),
        chq: "",
        chq_amt: Number(estimate.cheque_amt || 0),
        online: "",
        online_amt: Number(estimate.online_amt || 0),
      };
      setPaymentDetails(paymentData);

      // Update discount if available from estimate
      if (estimate.discount_percentage) {
        setDiscount(parseFloat(estimate.discount_percentage));
        localStorage.setItem(
          `discount`,
          estimate.discount_percentage.toString()
        );
      }

      // Process and store products
      if (products && products.length > 0) {
        // Use the same mapping logic as EstimateForm
        const mappedProducts = products.map((product) => {
          const grossWeight = parseFloat(product.gr_wt) || 0;
          const stoneWeight = parseFloat(product.st_wt) || 0;
          const weightBW = grossWeight - stoneWeight;
          const vaPercent = parseFloat(product.va_perc) || 0;
          const wastageWeight = (grossWeight * vaPercent) / 100;
          const totalWeightAV = weightBW + wastageWeight;
          const rate = parseFloat(product.rate) || 0;
          const rateAmt = rate * totalWeightAV;
          const stonePrice = parseFloat(product.stone_price) || 0;
          const makingCharges = parseFloat(product.mc) || 0;
          const hmCharges = parseFloat(product.hm_charges) || 60;
          const discount = parseFloat(product.discount) || 0;
          const festivalDiscount = parseFloat(product.fest_discount) || 0;
          const taxPercent = parseFloat(product.tax_perc) || 3;

          const totalBeforeTax =
            rateAmt +
            stonePrice +
            makingCharges +
            hmCharges -
            discount -
            festivalDiscount;
          const taxAmt = (totalBeforeTax * taxPercent) / 100;
          const totalPrice = totalBeforeTax + taxAmt;

          return {
            id: "", // Always empty ID when loading from estimate to create new sales record
            code: product.barcode || "",
            product_name: product.product_name || "",
            metal_type: product.metal || "",
            purity: product.purity || "",
            gross_weight: grossWeight.toFixed(2),
            stone_weight: stoneWeight.toFixed(2),
            va_percent: vaPercent.toFixed(2),
            total_weight_av: totalWeightAV.toFixed(2),
            weight_bw: weightBW.toFixed(2),
            wastage_weight: wastageWeight.toFixed(2),
            rate: rate.toFixed(2),
            rate_amt: rateAmt.toFixed(2),
            making_charges: makingCharges.toFixed(2),
            stone_price: stonePrice.toFixed(2),
            hm_charges: hmCharges.toFixed(2),
            disscount: discount.toFixed(2),
            festival_discount: festivalDiscount.toFixed(2),
            tax_percent: `${taxPercent}% GST`,
            tax_amt: taxAmt.toFixed(2),
            total_price: totalPrice.toFixed(2),
            imagePreview: product.image || "",
            status: product.status || "active",
            remarks: product.remarks || "",
            remarks_type: product.remarks_type || "",
            qty: (product.qty || 1).toString(),
            category: product.category || "",
            sub_category: product.sub_category || "",
            design_name: product.design_name || "",
            va_on: "Gross Weight",
            mc_on: "MC %",
            mc_per_gram:
              rateAmt > 0 ? ((makingCharges / rateAmt) * 100).toFixed(2) : "0",
            pricing: "By Weight",
            piece_taxable_amt: totalBeforeTax.toFixed(2),
            sale_status: "Delivered",
            estimate_status: "Not Delivered",
            transaction_status: "Sales",
            date: estimate.date || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
            estimate_number: estimate.estimate_number || "",
            invoice_number: updatedFormData.invoice_number || "",
            client_visibility: "Internal Facing",
          };
        });

        // Optional: Filter products based on stock availability if stock data is available
        let finalProducts = mappedProducts;
        if (stock && stock.length > 0) {
          const filteredProducts = mappedProducts.filter((product) => {
            const stockItem = stock.find(
              (stockItem) => stockItem.PCode_BarCode === product.code
            );
            if (!stockItem) {
              return true; // Include products not found in stock
            }
            if (stockItem.Status !== "Available") {
              return true; // Include products regardless of status
            }
            return true; // Include all products
          });

          finalProducts = filteredProducts;
        }

        // Store products in localStorage and state
        const storageKey = `repairDetails`;
        localStorage.setItem(storageKey, JSON.stringify(finalProducts));
        setRepairDetails(finalProducts);

        // Set flag to auto-edit first product when repairDetails state updates
        if (finalProducts.length > 0) {
          setShouldAutoEditFirstProduct(true);
        }

        // Store product count for success message
        const productCount = finalProducts.length;
      } else {
        const storageKey = `repairDetails`;
        localStorage.removeItem(storageKey);
        setRepairDetails([]);
        alert("No products found in the selected estimate.");
      }

      setEstimateDetails(res.data);

      // Fetch old items for this estimate
      try {
        const oldItemsResponse = await axios.get(
          `${baseURL}/get/olditems/${estimate_number}`
        );

        if (oldItemsResponse.data && oldItemsResponse.data.length > 0) {
          // Map the response data to the format expected by OldSalesForm
          const mappedOldItems = oldItemsResponse.data.map((item) => ({
            product: item.product || "",
            metal: item.metal || "",
            purity: item.purity || "",
            purityPercentage: item.purity_percentage || "",
            hsn_code: item.hsn_code || "",
            gross: parseFloat(item.gross) || 0,
            dust: parseFloat(item.dust) || 0,
            ml_percent: parseFloat(item.ml_percent) || 0,
            net_wt: parseFloat(item.net_wt) || 0,
            remarks: item.remarks || "",
            rate: parseFloat(item.rate) || 0,
            total_amount: parseFloat(item.total_amount) || 0,
            total_old_amount: oldItemsResponse.data.reduce(
              (sum, i) => sum + parseFloat(i.total_amount || 0),
              0
            ),
          }));

          setOldSalesData(mappedOldItems);
          setOldTableData(mappedOldItems);
          localStorage.setItem(
            `oldTableData`,
            JSON.stringify(mappedOldItems)
          );

          // Reset manual flag when URD data is loaded to allow automatic updates
          if (mappedOldItems.length > 0) {
            setIsManualNetPayableSet(false);
          }
        } else {
          setOldSalesData([]);
          setOldTableData([]);
          localStorage.setItem(`oldTableData`, JSON.stringify([]));

          // Reset manual flag when URD data is cleared
          setIsManualNetPayableSet(false);
        }
      } catch (error) {
        console.error("Error fetching old items:", error);
        setOldSalesData([]);
        setOldTableData([]);

        // Reset manual flag on error
        setIsManualNetPayableSet(false);
      }

      // Fetch sales return data for this estimate
      await fetchSalesReturnData(estimate_number);

      // Show success message
      const customerName =
        account.account_name || estimate.account_name || "Unknown Customer";
      const estimateDate = estimate.date
        ? formatDateForInput(estimate.date)
        : new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
      const oldItemsCount = oldSalesData.length;
      const oldItemsMessage =
        oldItemsCount > 0 ? `\nOld Items: ${oldItemsCount} item(s)` : "";
    } catch (err) {
      console.error("Failed to fetch estimate details:", err);
      alert(`Failed to load estimate ${estimate_number}. Please try again.`);
    }
  };

  // Fetch existing sales return data for the current estimate
  const fetchSalesReturnData = async (estimateNumber) => {
    if (!estimateNumber) {
      setSalesReturnData([]);
      return;
    }

    try {
      // Use the estimate sales return endpoint
      const response = await axios.get(
        `${baseURL}/get-estimate-sale-returns/${estimateNumber}`
      );

      setSalesReturnData(response.data || []);
    } catch (error) {
      console.error("Error fetching sales return data:", error);
      setSalesReturnData([]);
    }
  };

  useEffect(() => {}, [repairDetails]);

  // Populate sales return UI with existing data
  useEffect(() => {
    if (salesReturnData && salesReturnData.length > 0) {
      // Get unique invoice numbers from sales return data
      const invoiceNumbers = [
        ...new Set(salesReturnData.map((item) => item.original_invoice_number)),
      ];

      if (invoiceNumbers.length > 0) {
        // Set the first invoice number as selected
        const selectedInvoiceNumber = invoiceNumbers[0];

        // Update return data to set the invoice number
        setReturnData((prev) => ({
          ...prev,
          invoice_number: selectedInvoiceNumber,
        }));

        // Update filtered invoices to include the invoice from sales return data
        const updateFilteredInvoices = async () => {
          try {
            // Get all unique invoices
            const response = await axios.get(
              `${baseURL}/get-unique-repair-details`
            );
            if (response.data) {
              const allInvoices = response.data.filter(
                (item) => item.transaction_status === "Sales"
              );

              // Add the invoice from sales return data if it's not already in the list
              const salesReturnInvoices = invoiceNumbers.map(
                (invoiceNumber) => ({
                  invoice_number: invoiceNumber,
                  transaction_status: "Sales",
                })
              );

              const combinedInvoices = [...allInvoices];
              salesReturnInvoices.forEach((salesReturnInvoice) => {
                if (
                  !combinedInvoices.find(
                    (inv) =>
                      inv.invoice_number === salesReturnInvoice.invoice_number
                  )
                ) {
                  combinedInvoices.push(salesReturnInvoice);
                }
              });

              setFilteredInvoices(combinedInvoices);
            }
          } catch (error) {
            console.error("Error updating filtered invoices:", error);
          }
        };

        updateFilteredInvoices();

        // Fetch invoice details for this invoice
        const fetchInvoiceDetailsForSalesReturn = async () => {
          try {
            const response = await axios.get(
              `${baseURL}/getsales/${selectedInvoiceNumber}`
            );
            if (response.data) {
              // Filter out items that are already returned
              const availableItems = response.data.filter(
                (item) => item.status !== "Sale Returned"
              );
              setInvoiceDetails(availableItems);

              // Find which items from this invoice are in the sales return data
              const returnedItemCodes = salesReturnData
                .filter(
                  (item) =>
                    item.original_invoice_number === selectedInvoiceNumber
                )
                .map((item) => item.code);

              // Set selected rows based on returned items
              const selectedRowIndices = availableItems
                .map((item, index) =>
                  returnedItemCodes.includes(item.code) ? index : -1
                )
                .filter((index) => index !== -1);

              setSelectedRows(selectedRowIndices);
              setIsAllSelected(
                selectedRowIndices.length === availableItems.length &&
                  availableItems.length > 0
              );
            }
          } catch (error) {
            console.error(
              "Error fetching invoice details for sales return:",
              error
            );
          }
        };

        fetchInvoiceDetailsForSalesReturn();
      }
    }
  }, [salesReturnData]);

  const handleEstimateChange = (e) => {
    const selectedValue = e.target.value;

    if (selectedValue) {
      // Check if there are existing products in the form
      if (repairDetails.length > 0) {
        const confirmLoad = window.confirm(
          "Loading an estimate will replace all current products in the sales form. Do you want to continue?"
        );
        if (!confirmLoad) {
          return; // Don't proceed if user cancels
        }
      }

      setSelectedEstimate(selectedValue);
      fetchEstimateDetails(selectedValue);
    } else {
      // Clear estimate-related data
      setEstimateDetails(null);
      localStorage.removeItem(`repairDetails`);
      setRepairDetails([]);

      // Reset form data to default values
      setFormData((prev) => ({
        ...prev,
        estimate_number: "",
        customer_id: "",
        account_name: "",
        mobile: "",
        email: "",
        address1: "",
        address2: "",
        city: "",
        pincode: "",
        state: "",
        state_code: "",
        aadhar_card: "",
        gst_in: "",
        pan_card: "",
      }));

      // Reset payment details
      setPaymentDetails({
        cash_amount: 0,
        card_amt: 0,
        chq: "",
        chq_amt: 0,
        online: "",
        online_amt: 0,
      });

      // Reset discount
      setDiscount(0);
      localStorage.removeItem(`discount`);

      // Clear old items data
      setOldSalesData([]);
      setOldTableData([]);
      localStorage.removeItem(`oldTableData`);

      // Clear sales return data
      setSalesReturnData([]);
      setSelectedRows([]);
      setIsAllSelected(false);
      setReturnData({
        invoice_number: "",
      });
      localStorage.removeItem(`salesReturnData`);
    }
  };

  const [orderData, setOrderData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [autoEditIndex, setAutoEditIndex] = useState(null);
  const [autoUpdateInProgress, setAutoUpdateInProgress] = useState(false);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${baseURL}/get-unique-order-details`);

      const filteredData = response.data.filter(
        (item) =>
          (item.transaction_status === "Orders" ||
            item.transaction_status === "ConvertedInvoice") &&
          item.invoice !== "Converted"
      );

      setOrderData(filteredData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching repair details:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    handleViewDetails();
  }, []);

  const handleViewDetails = async (order_number) => {
    try {
      const response = await axios.get(
        `${baseURL}/get-order-details/${order_number}`
      );

      // Filter repeatedData to include only items where transaction_status is "Orders"
      const filteredData = response.data.repeatedData.filter(
        (item) => item.transaction_status === "Orders"
      );

      // Check if any item in repeatedData has invoice === "Converted"
      const isInvoiceConverted = filteredData.some(
        (item) => item.invoice === "Converted"
      );

      // Set state with filtered repeatedData and invoice status
      setOrderDetails({
        ...response.data,
        repeatedData: filteredData,
        isInvoiceConverted,
      });

      setShowModal(true);
    } catch (error) {
      console.error("Error fetching repair details:", error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setOrderDetails(null);
  };

  const fetchOrderDetails = async (order_number) => {
    try {
      const response = await axios.get(
        `${baseURL}/get-order-details/${order_number}`
      );

      // Filter only matching repeatedData items
      const filteredData = response.data.repeatedData.map((item) => ({
        ...item,
        invoice_number: formData.invoice_number, // Add invoice_number from formData
        transaction_status: "Orders", // Add transaction_status as "Sales"
        date: formData.date,
        // invoice: 'Converted',
      }));

      if (filteredData.length > 0) {
        // Store filtered data in localStorage
        localStorage.setItem(
          `repairDetails`,
          JSON.stringify(filteredData)
        );

        // Update state with filtered data
        setRepairDetails(filteredData);
        setAutoEditIndex(0);

        // Immediately retrieve and log stored data
        const storedData = JSON.parse(
          localStorage.getItem(`repairDetails`)
        );
        return filteredData;
      } else {
        // Clear localStorage if no matching data
        localStorage.removeItem(`repairDetails`);
        setRepairDetails([]); // Clear state
      }
    } catch (error) {
      console.error("Error fetching selected estimate details:", error);
    }
  };

  const handleOrderChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedOrder(selectedValue);

    if (selectedValue) {
      fetchOrderDetails(selectedValue);
    } else {
      setOrderDetails(null);
      localStorage.removeItem(`repairDetails`);
      setRepairDetails([]);
    }
  };

  const handleOrderCheckboxChange = async (e, order_number) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      // Fetch order details
      const orderDetails = await fetchOrderDetails(order_number);

      // Redirect to Order page with order details in state

      // set orderDetails in localStorage
      localStorage.setItem("orderDetails", JSON.stringify(orderDetails));
      window.open("/orders", "_blank");
    } else {
      // Clear selection
      setOrderDetails([]);
      setSelectedOrder("");
      setAutoEditIndex(null);
    }
  };

  useEffect(() => {
    if (repairDetails.length > 0 && autoEditIndex !== null) {
      handleEdit(autoEditIndex); // Trigger edit for current index
      setAutoUpdateInProgress(true); // Enable update trigger
    }
  }, [repairDetails, autoEditIndex]);

  useEffect(() => {
    if (autoUpdateInProgress) {
      handleUpdate(); // Update after editing
      setAutoUpdateInProgress(false); // Reset update trigger
    }
  }, [formData]);

  useEffect(() => {
    if (!autoUpdateInProgress && autoEditIndex !== null) {
      const nextIndex = autoEditIndex + 1;
      if (nextIndex < repairDetails.length) {
        setAutoEditIndex(nextIndex); // Proceed to next item
      } else {
        setAutoEditIndex(null); // Finished all updates
      }
    }
  }, [autoUpdateInProgress]);

  const [repairs, setRepairs] = useState([]);
  const [selectedRepairs, setSelectedRepairs] = useState({});

  const fetchRepairs = async () => {
    try {
      const response = await axios.get(`${baseURL}/get/repairs`);
      setRepairs(response.data);
    } catch (error) {
      console.error("Error fetching repairs:", error);
    }
  };

  useEffect(() => {
    fetchRepairs();
  }, []);

  const fetchRepairDetails = async (repair) => {
    try {
      const storageKey = `repairDetails`;

      // Create new filtered entry with necessary fields
      const filteredData = [
        {
          sub_category: repair.item,
          product_name: repair.item,
          customer_id: repair.customer_id,
          account_name: repair.account_name,
          mobile: repair.mobile,
          email: repair.email,
          address1: repair.address1,
          address2: repair.address2,
          city: repair.city,
          metal_type: repair.metal_type,
          purity: repair.purity,
          category: repair.category,
          gross_weight: "1",
          stone_weight: "0",
          stone_price: "0",
          weight_bw: "0",
          va_on: "Gross Weight",
          va_percent: "0",
          wastage_weight: "0",
          total_weight_av: "1",
          mc_on: "MC %",
          disscount_percentage: "0",
          disscount: "0",
          mc_per_gram: "0",
          making_charges: "0",
          printing_purity: repair.purity,
          selling_purity: repair.purity,
          qty: repair.qty,
          total_price: repair.total_amt,
          repair_no: repair.repair_no,
          invoice_number: formData.invoice_number,
          transaction_status: "Repairs",
          date: formData.date,
          invoice: "Converted",
          rate: repair.total_amt,
          tax_percent: "0",
          tax_amt: "0",
          hm_charges: "0",
        },
      ];

      // Store in localStorage
      localStorage.setItem(storageKey, JSON.stringify(filteredData));

      // Update state
      setRepairDetails(filteredData);
      setAutoEditIndex(0);

      // Debug log
      const storedData = JSON.parse(localStorage.getItem(storageKey));

      return filteredData;
    } catch (error) {
      console.error("Error fetching repair details:", error);
    }
  };

  const handleRepairCheckboxChange = async (e, repair_no) => {
    const isChecked = e.target.checked;
    const storageKey = `repairDetails`;

    const repair = repairs.find((r) => r.repair_no === repair_no);
    if (!repair) return console.warn("Repair not found");

    if (isChecked) {
      localStorage.removeItem(storageKey);
      setRepairDetails([]);
      setSelectedRepairs({});

      await fetchRepairDetails(repair);

      setSelectedRepairs((prev) => ({
        ...prev,
        [repair.repair_no]: true,
      }));

      setSelectedOrder(""); // Optional reset
    } else {
      localStorage.removeItem(storageKey);
      setRepairDetails([]);
      setSelectedRepairs((prev) => {
        const updated = { ...prev };
        delete updated[repair.repair_no];
        return updated;
      });
    }
  };

  // Function to apply festival discount to a single product
  const applyFestivalDiscountToProduct = (product, offer) => {
    const taxPercent = parseFloat(product.tax_percent) || 1;
    const pieceCost = parseFloat(product.pieace_cost) || 0;
    const qty = parseFloat(product.qty) || 1;
    const discountAmt = parseFloat(product.disscount) || 0;
    const pricingType = product.pricing;
    const rateAmt = parseFloat(product.rate_amt) || 0;
    const stonePrice = parseFloat(product.stone_price) || 0;
    const makingCharges = parseFloat(product.making_charges) || 0;
    const hmCharges = parseFloat(product.hm_charges) || 0;
    const grossWeight = parseFloat(product.gross_weight) || 0;

    const percentageDiscount = parseFloat(offer.discount_percentage) || 0;
    const rateDiscount = parseFloat(offer.discount_on_rate) || 0;
    const fixedPercentageDiscount =
      parseFloat(offer.discount_percent_fixed) || 0;
    const weightBasedDiscount = (rateDiscount / 10) * grossWeight;
    const totalDiscountValue =
      pricingType === "By fixed"
        ? fixedPercentageDiscount
        : percentageDiscount + weightBasedDiscount;

    if (pricingType === "By fixed") {
      const pieceTaxableAmt = pieceCost * qty;
      const originalPieceTaxableAmt = product.original_piece_taxable_amt
        ? parseFloat(product.original_piece_taxable_amt)
        : pieceTaxableAmt;

      const calculatedDiscount = (pieceTaxableAmt * totalDiscountValue) / 100;
      const updatedPieceTaxableAmt =
        originalPieceTaxableAmt - calculatedDiscount - discountAmt;
      const taxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
      const totalPrice = updatedPieceTaxableAmt + taxAmt;

      return {
        ...product,
        original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2),
        festival_discount: calculatedDiscount.toFixed(2),
        festival_discount_percentage: percentageDiscount.toFixed(2),
        festival_discount_on_rate: rateDiscount.toFixed(2),
        piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
        tax_amt: taxAmt.toFixed(2),
        total_price: totalPrice.toFixed(2),
      };
    } else {
      const calculatedDiscount =
        (makingCharges * percentageDiscount) / 100 +
        (rateDiscount / 10) * grossWeight;
      const totalBeforeTax =
        rateAmt +
        stonePrice +
        makingCharges +
        hmCharges -
        calculatedDiscount -
        discountAmt;

      const taxAmt = (totalBeforeTax * taxPercent) / 100;
      const updatedTotalPrice = totalBeforeTax + taxAmt;

      return {
        ...product,
        original_total_price: product.original_total_price
          ? product.original_total_price
          : parseFloat(product.total_price).toFixed(2),
        festival_discount: calculatedDiscount.toFixed(2),
        festival_discount_percentage: percentageDiscount.toFixed(2),
        festival_discount_on_rate: rateDiscount.toFixed(2),
        tax_amt: taxAmt.toFixed(2),
        total_price: updatedTotalPrice.toFixed(2),
      };
    }
  };

  // Function to apply festival discount to all products
  const applyFestivalDiscountToAllProducts = () => {
    console.log("applyFestivalDiscountToAllProducts called");
    console.log("offers:", offers);
    console.log("appliedOffersFromAPI:", appliedOffersFromAPI);
    console.log("repairDetails:", repairDetails);

    if (offers.length === 0 && appliedOffersFromAPI.length === 0) {
      console.log("No offers available");
      return;
    }

    // Use applied offers from API if available, otherwise use first offer
    const activeOffer =
      appliedOffersFromAPI.length > 0
        ? appliedOffersFromAPI[0]
        : offers.length > 0
        ? offers[0]
        : null;

    console.log("Active offer:", activeOffer);

    if (!activeOffer) {
      console.log("No active offer found");
      return;
    }

    // Use the existing handleApply logic which is already working correctly
    const storedRepairDetails =
      JSON.parse(localStorage.getItem(`repairDetails`)) || [];
    let updatedRepairDetails =
      storedRepairDetails.length > 0
        ? [...storedRepairDetails]
        : [...repairDetails];

    console.log("Products to update:", updatedRepairDetails.length);

    if (updatedRepairDetails.length === 0) {
      console.log("No products to update");
      return;
    }

    updatedRepairDetails = updatedRepairDetails.map((item, index) => {
      console.log(`Processing product ${index + 1}:`, item);
      const taxPercent = parseFloat(item.tax_percent) || 1;
      const pieceCost = parseFloat(item.pieace_cost) || 0;
      const qty = parseFloat(item.qty) || 1;
      const discountAmt = parseFloat(item.disscount) || 0;
      const pricingType = item.pricing;
      const rateAmt = parseFloat(item.rate_amt) || 0;
      const stonePrice = parseFloat(item.stone_price) || 0;
      const makingCharges = parseFloat(item.making_charges) || 0;
      const hmCharges = parseFloat(item.hm_charges) || 0;
      const grossWeight = parseFloat(item.gross_weight) || 0;

      const percentageDiscount =
        parseFloat(activeOffer.discount_percentage) || 0;
      const rateDiscount = parseFloat(activeOffer.discount_on_rate) || 0;
      const fixedPercentageDiscount =
        parseFloat(activeOffer.discount_percent_fixed) || 0;
      const weightBasedDiscount = (rateDiscount / 10) * grossWeight;
      const totalDiscountValue =
        pricingType === "By fixed"
          ? fixedPercentageDiscount
          : percentageDiscount + weightBasedDiscount;

      if (pricingType === "By fixed") {
        const pieceTaxableAmt = pieceCost * qty;
        const originalPieceTaxableAmt = item.original_piece_taxable_amt
          ? parseFloat(item.original_piece_taxable_amt)
          : pieceTaxableAmt;

        const calculatedDiscount = (pieceTaxableAmt * totalDiscountValue) / 100;
        const updatedPieceTaxableAmt =
          originalPieceTaxableAmt - calculatedDiscount - discountAmt;
        const taxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
        const totalPrice = updatedPieceTaxableAmt + taxAmt;

        return {
          ...item,
          original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2),
          festival_discount: calculatedDiscount.toFixed(2),
          festival_discount_percentage: percentageDiscount.toFixed(2),
          festival_discount_on_rate: rateDiscount.toFixed(2),
          piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: totalPrice.toFixed(2),
        };
      } else {
        const calculatedDiscount =
          (makingCharges * percentageDiscount) / 100 +
          (rateDiscount / 10) * grossWeight;
        const totalBeforeTax =
          rateAmt +
          stonePrice +
          makingCharges +
          hmCharges -
          calculatedDiscount -
          discountAmt;

        const taxAmt = (totalBeforeTax * taxPercent) / 100;
        const updatedTotalPrice = totalBeforeTax + taxAmt;

        return {
          ...item,
          original_total_price: item.original_total_price
            ? item.original_total_price
            : parseFloat(item.total_price).toFixed(2),
          festival_discount: calculatedDiscount.toFixed(2),
          festival_discount_percentage: percentageDiscount.toFixed(2),
          festival_discount_on_rate: rateDiscount.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: updatedTotalPrice.toFixed(2),
        };
      }
    });

    setRepairDetails(updatedRepairDetails);
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(updatedRepairDetails)
    );
  };
  const validateFields = useCallback(async() => {
    const errors = [];

    // Validate Barcode/Rbarcode
    if (!formData.code || formData.code.trim() === "") {
      errors.push("BarCode/Rbarcode is required");
    }

    // Validate Gross Weight for "By Weight" and "By Running Tag" pricing
    if (formData.pricing !== "By fixed") {
      if (!formData.gross_weight || parseFloat(formData.gross_weight) <= 0) {
        errors.push("Gross Weight is required and must be greater than 0");
      }
    }

    if(formData.pricing=== "By Running Tag"){
      try {
        // Check local stock first (tracks real-time remaining pieces)
        const localStockData = localStock[String(formData.code)] || {};
        const localRemPcs = localStockData.rem_pcs !== undefined && localStockData.rem_pcs !== null
          ? localStockData.rem_pcs
          : null;

        if (localRemPcs !== null) {
          // Use local stock if available
          if (Number(localRemPcs) <= 0) {
            errors.push("The product is already sold out!");
          } else if (Number(localRemPcs) < Number(formData.qty)) {
            errors.push(
              `Qty cannot exceed available pcs (${localRemPcs} pcs available)`
            );
          }
        } else {
          // Fallback to server data if local stock not available
          const response = await fetch(`${baseURL}/get/opening-tags-entry`);
          if (!response.ok) throw new Error("Failed to fetch tag data");

          const result = await response.json();
          const tags = result.result || [];

          const tag = tags.find(
            (t) => String(t.PCode_BarCode) === String(formData.code)
          );

          if (tag) {
            const tagRemPcs = tag.rem_pcs !== undefined && tag.rem_pcs !== null
              ? tag.rem_pcs
              : tag.pcs;
            
            if (Number(tagRemPcs) <= 0) {
              errors.push("The product is already sold out!");
            } else if (Number(tagRemPcs) < Number(formData.qty)) {
              errors.push(
                `Qty cannot exceed available pcs (${tagRemPcs} pcs available)`
              );
            }
          }
        }

      } catch (err) {
        console.error("TAG validation failed:", err);
        errors.push("Error validating tag availability");
      }
    }    

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return false;
    }

    return true;
  });

  const handleAdd = useCallback(async () => {
      const isValid = await validateFields();  //Validate fields before adding
  
      if (!isValid) {
        return null;   
      }
    const wasManualLocked = isManualNetPayableSet;
    const storedRepairDetails =
      JSON.parse(localStorage.getItem(`repairDetails`)) || [];

      console.log("formData new", formData);
    // Create the new product
    const newProduct = {
      ...formData,
      date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
      pieace_cost:
        formData.pieace_cost && parseFloat(formData.pieace_cost) > 0
          ? parseFloat(formData.pieace_cost).toFixed(2)
          : null,
      rate:
        formData.rate && parseFloat(formData.rate) > 0
          ? parseFloat(formData.rate).toFixed(2)
          : "",
      imagePreview: formData.imagePreview,
      client_visibility: formData.client_visibility,
    };

    // Add new repair detail
    const updatedRepairDetails = [...repairDetails, newProduct];

    setRepairDetails(updatedRepairDetails);
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(updatedRepairDetails)
    );

    // Update local stock if product has a barcode (tag entry)
    if (formData.code && formData.qty) {
      updateLocalStock(formData.code, formData.qty);
    }

    // Reset form data
    setFormData((prevData) => ({
      ...prevData,
      disscount: "",
      disscount_percentage: "",
      pieace_cost: "",
      imagePreview: null,
      sale_status: "Delivered",
      piece_taxable_amt: "",
      festival_discount: "",
    }));

    resetProductFields();

    // When a new product is added after a manual Net Payable save,
    // exit manual-net mode to avoid compounding discount calculations
    setIsManualNetMode(false);
    setManualNetAmount(0);
    setIsManualNetPayableSet(false);
    setSavedNetPayableAmount(0);

    // Make net payable amount editable when product is added
    if (setNetPayableEditableRef.current) {
      setNetPayableEditableRef.current();
    }

    // Apply festival discount to all products if offers are available
    if (offers.length > 0 || appliedOffersFromAPI.length > 0) {
      applyFestivalDiscountToAllProducts();
    }

    // If manual net payable had been locked previously, clear the discount
    // so it doesn't stack with the newly added product
    if (wasManualLocked) {
      setDiscount(0);
      applyDiscountToRepairDetails(0);
    } else if (discount !== "" && !isNaN(parseFloat(discount))) {
      // Otherwise, re-apply existing percentage uniformly
      applyDiscountToRepairDetails(parseFloat(discount));
    }
  });
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleEdit = (index) => {
    // Check if repairDetails exists and has items
    if (
      !repairDetails ||
      !Array.isArray(repairDetails) ||
      repairDetails.length === 0
    ) {
      console.warn("No repair details available for editing");
      return;
    }

    // Check if the index is valid
    if (index < 0 || index >= repairDetails.length) {
      console.warn(
        `Invalid index ${index} for repairDetails array of length ${repairDetails.length}`
      );
      return;
    }

    const item = repairDetails[index];

    // Check if item exists and has required properties
    if (!item) {
      console.warn(`Item at index ${index} is undefined`);
      return;
    }

    setEditIndex(index);

    const pieceCost = parseFloat(item.pieace_cost) || 0;
    const qty = parseFloat(item.qty) || 1;
    const pieceTaxableAmt = parseFloat(item.piece_taxable_amt || 0).toFixed(2);

    // For "By Running Tag", clear selling_purity so it gets recalculated from components
    const editedItem = { ...item };
    if (item.pricing === "By Running Tag") {
      editedItem.selling_purity = "";
      editedItem.custom_purity = "";
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      ...editedItem,
      date:formData.date,
      pieace_cost: pieceCost > 0 ? pieceCost.toFixed(2) : "",
      rate:
        item.rate && parseFloat(item.rate) > 0
          ? parseFloat(item.rate).toFixed(2)
          : "",
      piece_taxable_amt: pieceTaxableAmt,
      client_visibility: item.client_visibility || "Internal Facing",
    }));//
  };

  const handleUpdate = () => {
    const wasManualLocked = isManualNetPayableSet;
    const updatedDetails = repairDetails.map((item, index) => {
      if (index === editIndex) {
        // Create the updated product with proper formatting
        const updatedProduct = {
          ...formData,
          pieace_cost:
            formData.pieace_cost && parseFloat(formData.pieace_cost) > 0
              ? parseFloat(formData.pieace_cost).toFixed(2)
              : null,
          rate:
            formData.rate && parseFloat(formData.rate) > 0
              ? parseFloat(formData.rate).toFixed(2)
              : "",
          imagePreview: formData.imagePreview,
          client_visibility: formData.client_visibility,
        };
        return updatedProduct;
      }
      return item;
    });

    setRepairDetails(updatedDetails);

    // Save to localStorage to persist the updated data
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(updatedDetails)
    );

    setEditIndex(null);
    resetProductFields();

    // Exit manual-net mode to prevent compounded discount after edits
    setIsManualNetMode(false);
    setManualNetAmount(0);
    setIsManualNetPayableSet(false);
    setSavedNetPayableAmount(0);

    // Make net payable amount editable when product is updated
    if (setNetPayableEditableRef.current) {
      setNetPayableEditableRef.current();
    }

    // Apply festival discount to all products if offers are available
    if (offers.length > 0 || appliedOffersFromAPI.length > 0) {
      applyFestivalDiscountToAllProducts();
    }

    // If manual net payable had been locked previously, clear the discount
    // so it doesn't stack with the newly updated product
    if (wasManualLocked) {
      setDiscount(0);
      applyDiscountToRepairDetails(0);
    } else if (discount !== "" && !isNaN(parseFloat(discount))) {
      // Otherwise, re-apply existing percentage uniformly
      applyDiscountToRepairDetails(parseFloat(discount));
    }
  };

  const handleDelete = (indexToDelete) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const wasManualLocked = isManualNetPayableSet;
      const deletedProduct = repairDetails[indexToDelete];
      
      // Restore local stock if product has a barcode (tag entry)
      if (deletedProduct && deletedProduct.code && deletedProduct.qty) {
        restoreLocalStock(deletedProduct.code, deletedProduct.qty);
      }
      
      setRepairDetails(
        repairDetails.filter((_, index) => index !== indexToDelete)
      );
      alert("Product deleted successfully");

      // Exit manual-net mode to prevent compounded discount after deletion
      setIsManualNetMode(false);
      setManualNetAmount(0);
      setIsManualNetPayableSet(false);
      setSavedNetPayableAmount(0);

      // Make net payable amount editable when product is deleted
      if (setNetPayableEditableRef.current) {
        setNetPayableEditableRef.current();
      }

      // If manual net payable had been locked previously, clear the discount
      // so it doesn't stack after deletion
      if (wasManualLocked) {
        setDiscount(0);
        applyDiscountToRepairDetails(0);
      } else if (discount !== "" && !isNaN(parseFloat(discount))) {
        // Otherwise, re-apply existing percentage uniformly
        applyDiscountToRepairDetails(parseFloat(discount));
      }
    }
  };

  const resetProductFields = () => {
    setFormData((prev) => ({
      ...prev,
      code: "",
      product_id: "",
      metal: "",
      product_name: "",
      metal_type: "",
      design_name: "",
      purity: "",
      selling_purity: "",
      printing_purity: "",
      category: "",
      sub_category: "",
      gross_weight: "",
      stone_weight: "",
      weight_bw: "",
      stone_price: "",
      va_on: "Gross Weight",
      va_percent: "",
      wastage_weight: "",
      total_weight_av: "",
      mc_on: "MC %",
      mc_per_gram: "",
      making_charges: "",
      disscount_percentage: "",
      disscount: "",
      rate: "",
      rate_amt: "",
      pricing: "By Weight",
      tax_percent: "03% GST",
      tax_amt: "",
      hm_charges: "60.00",
      total_price: "",
      qty: "",
      imagePreview: null,
      remarks: "",
      sale_status: "Delivered",
      piece_taxable_amt: "",
      festival_discount: "",
      client_visibility: "Internal Facing",
    }));
  };

  // Calculate totalPrice (sum of total_price from all repairDetails)
  const totalPrice = repairDetails.reduce(
    (sum, item) => sum + parseFloat(item.total_price || 0),
    0
  );

  const [oldTableData, setOldTableData] = useState([]);

  const [schemeTableData, setSchemeTableData] = useState([]);

  // Save states to localStorage after they are defined
  useEffect(() => {
    localStorage.setItem(
      `schemeTableData`,
      JSON.stringify(schemeTableData)
    );
  }, [schemeTableData]);

  useEffect(() => {
    localStorage.setItem(
      `oldTableData`,
      JSON.stringify(oldTableData)
    );
  }, [oldTableData]);

  useEffect(() => {
    localStorage.setItem(
      `salesReturnData`,
      JSON.stringify(salesReturnData)
    );
  }, [salesReturnData]);

  const resetForm = () => {
    setFormData({
      customer_id: "",
      mobile: "",
      account_name: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      pincode: "",
      state: "",
      state_code: "",
      aadhar_card: "",
      gst_in: "",
      pan_card: "",
      date: "",
      invoice_number: "",
    });
    setPaymentDetails({
      cash_amount: 0,
      card_amt: 0,
      chq: "",
      chq_amt: 0,
      online: "",
      online_amt: 0,
    });

    localStorage.removeItem(`repairDetails`);
    localStorage.removeItem(`paymentDetails`);
    localStorage.removeItem(`oldTableData`);
    localStorage.removeItem(`schemeTableData`);
    localStorage.removeItem(`salesReturnData`);
    clearLocalStock(); // Clear local stock on form reset
    window.location.reload();
  };

  const [totalAdvanceAmount, setTotalAdvanceAmount] = useState(0);

  useEffect(() => {
    if (repairDetails && repairDetails.length > 0) {
      const lastRepair = repairDetails[repairDetails.length - 1];
      if (lastRepair.advance_amt) {
        try {
          const advances = JSON.parse(lastRepair.advance_amt);
          setTotalAdvanceAmount(
            advances.reduce((sum, entry) => sum + Number(entry.amount || 0), 0)
          );
        } catch {
          setTotalAdvanceAmount(0);
        }
      } else {
        setTotalAdvanceAmount(0);
      }
    }
  }, [repairDetails]);
  const resetSaleReturnForm = () => {
    setReturnData({
      invoice_number: "",
    });
  };

  const handleBack = () => {
    navigate("/salestable");
  };

  // const handleAddCustomer = () => {
  //   navigate("/customermaster", { state: { from: `/sales?tabId=${tabId}` } });
  // };

  const handleAddCustomer = (mobile) => {
    navigate("/customermaster", {
      state: {
        from: `/sales`,
        mobile: mobile,
      },
    });
  };

  const [manualNetAmount, setManualNetAmount] = useState(0);
  const [manualNetPayAmount, setManualNetPayAmount] = useState(0);
  const [oldItemsAmount, setOldItemsAmount] = useState(0);
  // const oldItemsAmount = oldSalesData.reduce(
  //   (sum, item) => sum + parseFloat(item.total_amount || 0),
  //   0
  // );
  useEffect(() => {
    let computedAmount = oldSalesData.reduce(
      (sum, item) => sum + parseFloat(item.total_amount || 0),
      0
    );
    if (
      location.state?.from === "orders" &&
      location.state?.orderDetails?.[0]?.old_exchange_amt != null
    ) {
      computedAmount = parseFloat(
        location.state.orderDetails[0].old_exchange_amt
      );
    }
    setOldItemsAmount(computedAmount);
  }, [oldSalesData, location.state]);
  useEffect(() => {
    console.log("Updated Old Items Amount:", oldItemsAmount);
  }, [oldItemsAmount]);

  useEffect(() => {
    const fetchOldItems = async () => {
      try {
        if (orderDetails?.[0]?.order_number) {
          const response = await axios.get(
            `${baseURL}/get/olditems/${orderDetails[0].order_number}`
          );

          if (response.data && Array.isArray(response.data)) {
            setOldSalesData(response.data);
            const total = response.data.reduce(
              (sum, item) => sum + parseFloat(item.total_amount || 0),
              0
            );
            //setOldItemsAmount(total);

            // Reset manual flag when URD data is loaded to allow automatic updates
            if (response.data.length > 0) {
              setIsManualNetPayableSet(false);
            }
          }
        } else {
        }
      } catch (error) {
        console.error("Error fetching old items:", error);

        // Reset manual flag on error
        setIsManualNetPayableSet(false);
      }
    };
    fetchOldItems();
  }, [orderDetails]);

  // Reset manual flag when URD data changes to allow automatic net payable updates
  useEffect(() => {
    if (oldSalesData.length > 0) {
      setIsManualNetPayableSet(false);
    }
  }, [oldSalesData]);

  //changed schemeAmount when coming from orderDetails
  const schemeAmount =
    location.state?.orderDetails?.[0]?.scheme_amt != null
      ? parseFloat(location.state.orderDetails[0].scheme_amt)
      : location.state?.scheme_amt != null
      ? parseFloat(location.state.scheme_amt)
      : schemeSalesData.reduce(
          (sum, item) => sum + parseFloat(item.paid_amount || 0),
          0
        );

  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const handleCheckboxChange = (event, index) => {
    if (!invoiceDetails.length) return;

    const isChecked = event.target.checked;
    let updatedSelectedRows = isChecked
      ? [...selectedRows, index]
      : selectedRows.filter((i) => i !== index);

    setSelectedRows(updatedSelectedRows);
    setIsAllSelected(updatedSelectedRows.length === invoiceDetails.length);
  };

  const handleSelectAllChange = (event) => {
    if (!invoiceDetails.length) return;

    const isChecked = event.target.checked;
    setSelectedRows(isChecked ? invoiceDetails.map((_, index) => index) : []);
    setIsAllSelected(isChecked);
  };

  const handleCheckout = async () => {
    if (!invoiceDetails.length || !selectedRows.length) {
      // alert("No invoices selected for sale return.");
      return;
    }

    try {
      const selectedInvoices = selectedRows.map(
        (rowIndex) => invoiceDetails[rowIndex]
      );

      const repairDetailsUpdates = selectedInvoices.map((invoice) => ({
        id: invoice.id,
        status: "Sale Returned",
      }));

      const openTagsUpdates = selectedInvoices.map((invoice) => ({
        PCode_BarCode: invoice.code,
        Status: "Sale Returned",
      }));

      const productUpdates = selectedInvoices.map((invoice) => ({
        product_id: invoice.product_id,
        qty: invoice.qty,
        gross_weight: invoice.gross_weight,
      }));

      const codesForAvailableEntries = selectedInvoices.map(
        (invoice) => invoice.code
      );

      // Execute all API calls in parallel
      const responses = await Promise.allSettled([
        axios.post(`${baseURL}/updateRepairDetails`, {
          updates: repairDetailsUpdates,
        }),
        axios.post(`${baseURL}/updateOpenTags`, { updates: openTagsUpdates }),
        axios.post(`${baseURL}/updateProduct`, { updates: productUpdates }),
        axios.post(`${baseURL}/addAvailableEntry`, {
          codes: codesForAvailableEntries,
        }),
      ]);

      // Check if any API failed
      const failedRequests = responses.filter(
        (res) => res.status === "rejected"
      );
      if (failedRequests.length > 0) {
        console.error("Some API calls failed:", failedRequests);
        alert("Some updates failed. Please check console for details.");
      } else {
        alert("Sale Return added Successfully!");
        // Clear selected rows after successful sale return
        setSelectedRows([]);
        setIsAllSelected(false);
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      alert("An error occurred during checkout. Please try again.");
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem(`repairDetails`);
    if (stored) {
      setRepairDetails(JSON.parse(stored));
    }
  }, []);

  const salesTaxableAmount = selectedRows.reduce((sum, rowIndex) => {
    const detail = invoiceDetails[rowIndex];
    const stonePrice = parseFloat(detail.stone_price) || 0;
    const makingCharges = parseFloat(detail.making_charges) || 0;
    const rateAmt = parseFloat(detail.rate_amt) || 0;
    const itemDiscount = parseFloat(detail.disscount) || 0;
    const itemFestivalDiscount = parseFloat(detail.festival_discount) || 0;
    return (
      sum +
      stonePrice +
      makingCharges +
      rateAmt -
      itemDiscount -
      itemFestivalDiscount
    );
  }, 0);

  const salesTaxAmount = selectedRows.reduce((sum, rowIndex) => {
    const detail = invoiceDetails[rowIndex];
    return sum + parseFloat(detail.tax_amt || 0);
  }, 0);

  const salesNetAmount = salesTaxableAmount + salesTaxAmount;

  // Calculate total sales return amount
  // For new sales returns (when items are selected), use the current UI selections
  // For existing sales returns (when no items are selected), use the saved data
  const totalSalesReturnAmount =
    selectedRows.length > 0
      ? salesNetAmount // Use current selection for new sales returns
      : salesReturnData && salesReturnData.length > 0
      ? salesReturnData.reduce(
          (sum, item) => sum + parseFloat(item.total_price || 0),
          0
        )
      : 0; // No sales return data

  // Debug sales return data

  const isSameMonth = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    return (
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  };

  const allSelectedRowsAreThisMonth = selectedRows.every((rowIndex) =>
    isSameMonth(invoiceDetails[rowIndex]?.date)
  );

  const salesAmountToPass =
    location.state?.orderDetails?.[0]?.sale_return_amt != null
      ? parseFloat(location.state.orderDetails[0].sale_return_amt)
      : allSelectedRowsAreThisMonth
      ? salesNetAmount
      : salesTaxableAmount;

  let totalAmount = 0;
  let discountAmt = 0;
  let festivalDiscountAmt = 0;
  let taxableAmount = 0;
  let taxAmount = 0;
  let netAmount = 0;
  let discountPercent = 0;
  let totalMakingCharges = 0;
  let totalPieceCost = 0;
  let hasWeightBasedItems = false;

  if (
    isManualNetMode &&
    typeof manualNetAmount !== "undefined" &&
    manualNetAmount > 0
  ) {
    // Step 1: Compute totalAmount, makingCharges, pieceCost

    repairDetails.forEach((item) => {
      const pricing = item.pricing;

      if (pricing === "By Weight") {
        const stonePrice = parseFloat(item.stone_price) || 0;
        const makingCharges = parseFloat(item.making_charges) || 0;
        const rateAmt = parseFloat(item.rate_amt) || 0;
        const hmCharges = parseFloat(item.hm_charges) || 0;
        totalAmount += stonePrice + makingCharges + rateAmt + hmCharges;

        totalMakingCharges += makingCharges;
        hasWeightBasedItems = true;
      } else {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 0;
        const itemTotal = pieceCost * qty;
        totalAmount += itemTotal;
        totalPieceCost += itemTotal;
      }
    });

    // Step 2: Reverse tax and discount calculation from netAmount
    let cumulativeTaxable = 0;
    let cumulativeTax = 0;
    let totalFestivalDiscount = 0;

    repairDetails.forEach((item) => {
      const pricing = item.pricing;
      const itemTaxPercent = parseFloat(item.tax_percent) || 0;
      const itemFestivalDiscount = parseFloat(item.festival_discount) || 0;
      totalFestivalDiscount += itemFestivalDiscount;

      let itemTotal = 0;
      if (pricing === "By Weight") {
        const stonePrice = parseFloat(item.stone_price) || 0;
        const makingCharges = parseFloat(item.making_charges) || 0;
        const rateAmt = parseFloat(item.rate_amt) || 0;
        const hmCharges = parseFloat(item.hm_charges) || 0;
        itemTotal = stonePrice + makingCharges + rateAmt + hmCharges;
      } else {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 0;
        itemTotal = pieceCost * qty;
      }

      const itemShare = itemTotal / totalAmount;
      const itemNet = manualNetAmount * itemShare;
      const itemTaxable = itemNet / (1 + itemTaxPercent / 100);
      const itemTax = itemNet - itemTaxable;

      cumulativeTaxable += itemTaxable;
      cumulativeTax += itemTax;
    });

    taxableAmount = cumulativeTaxable;
    taxAmount = cumulativeTax;
    netAmount = manualNetAmount;
    festivalDiscountAmt = totalFestivalDiscount;

    discountAmt = totalAmount - taxableAmount - totalFestivalDiscount;

    const discountBase = totalMakingCharges + totalPieceCost;
    discountPercent = discountBase ? (discountAmt * 100) / discountBase : 0;

    const roundedDiscount = parseFloat(discountPercent.toFixed(2));

    if (roundedDiscount !== discount) {
      setDiscount(roundedDiscount);
      localStorage.setItem(`discount`, roundedDiscount.toString());
      applyDiscountToRepairDetails(roundedDiscount);
    }
  } else {
    // FORWARD CALCULATION
    repairDetails.forEach((item) => {
      const pricing = item.pricing;
      const itemDiscount = parseFloat(item.disscount) || 0;
      const itemFestivalDiscount = parseFloat(item.festival_discount) || 0;
      const itemTaxPercent = parseFloat(item.tax_percent) || 0;

      let itemTotal = 0;
      if (pricing === "By Weight") {
        const stonePrice = parseFloat(item.stone_price) || 0;
        const makingCharges = parseFloat(item.making_charges) || 0;
        const rateAmt = parseFloat(item.rate_amt) || 0;
        const hmCharges = parseFloat(item.hm_charges) || 0;

        itemTotal = stonePrice + makingCharges + rateAmt + hmCharges;
        totalAmount += itemTotal;
        totalMakingCharges += makingCharges;
        hasWeightBasedItems = true;
      } else {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 0;

        itemTotal = pieceCost * qty;
        totalAmount += itemTotal;
        totalPieceCost += itemTotal;
      }

      const totalDiscount = itemDiscount + itemFestivalDiscount;
      const itemTaxable = itemTotal - totalDiscount;
      const taxAmt = (itemTaxable * itemTaxPercent) / 100;

      discountAmt += itemDiscount;
      festivalDiscountAmt += itemFestivalDiscount;

      taxableAmount += itemTaxable;
      taxAmount += taxAmt;
      netAmount += itemTaxable + taxAmt;
    });

    const discountBase = totalMakingCharges + totalPieceCost;
    discountPercent = discountBase ? (discountAmt * 100) / discountBase : 0;
  }

  const handleManualNetAmountChange = (value) => {
    setManualNetAmount(value);
    setIsManualNetMode(true); // Enter manual net amount mode
  };

  const handleManualNetPayAmountChange = (value) => {
    setManualNetPayAmount(value);
    setIsManualNetMode(true);

    // Calculate total amount without any discounts
    let totalAmountWithoutDiscounts = 0;
    let totalMakingCharges = 0;
    let totalPieceCost = 0;

    repairDetails.forEach((item) => {
      const pricing = item.pricing;
      const itemFestivalDiscount = parseFloat(item.festival_discount) || 0;

      if (pricing === "By Weight") {
        const stonePrice = parseFloat(item.stone_price) || 0;
        const makingCharges = parseFloat(item.making_charges) || 0;
        const rateAmt = parseFloat(item.rate_amt) || 0;
        const hmCharges = parseFloat(item.hm_charges) || 0;
        totalAmountWithoutDiscounts +=
          stonePrice + makingCharges + rateAmt + hmCharges;
        totalMakingCharges += makingCharges;
      } else {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 0;
        const itemTotal = pieceCost * qty;
        totalAmountWithoutDiscounts += itemTotal;
        totalPieceCost += itemTotal;
      }
    });

    // Check if deductions are present
    const hasDeductions =
      oldItemsAmount > 0 ||
      schemeAmount > 0 ||
      salesAmountToPass > 0 ||
      totalAdvanceAmount > 0;

    if (hasDeductions) {
      // When deductions are present, calculate discount based on the difference between
      // the original net amount and the target net payable amount

      // Calculate the target net amount (net payable + deductions)
      const targetNetAmount =
        value +
        oldItemsAmount +
        schemeAmount +
        salesAmountToPass +
        totalAdvanceAmount;

      // Calculate the discount as the difference between original net amount and target net amount
      const discountAmount = Math.max(0, netAmount - targetNetAmount);

      // Calculate discount percentage based on making charges and piece cost only
      const discountBase = totalMakingCharges + totalPieceCost;
      const discountPercent =
        discountBase > 0 ? (discountAmount * 100) / discountBase : 0;

      const roundedDiscount = parseFloat(discountPercent.toFixed(2));

      // Ensure discount is not negative
      const finalDiscount = Math.max(0, roundedDiscount);

      console.log("Discount Calculation with Deductions:", {
        originalNetAmount: netAmount,
        targetNetAmount,
        discountAmount,
        discountBase,
        discountPercent,
        finalDiscount,
        oldItemsAmount,
        schemeAmount,
        totalSalesReturnAmount,
        totalAdvanceAmount,
        netPayableAmount: value,
      });

      if (finalDiscount !== discount) {
        setDiscount(finalDiscount);
        localStorage.setItem(`discount`, finalDiscount.toString());
        applyDiscountToRepairDetails(finalDiscount, true); // Pass true to preserve festival discount
      }

      // Set the manual net amount to the target net amount
      setManualNetAmount(targetNetAmount);
    } else {
      // Original logic for when no deductions are present
      // Calculate what the netAmount should be to achieve this netPayAmount
      const calculatedNetAmount = value;

      // Calculate the required taxable amount after preserving festival discount
      let totalTaxableAmount = 0;
      let totalTaxAmount = 0;

      repairDetails.forEach((item) => {
        const pricing = item.pricing;
        const itemTaxPercent = parseFloat(item.tax_percent) || 0;
        const itemFestivalDiscount = parseFloat(item.festival_discount) || 0;

        let itemTotal = 0;
        if (pricing === "By Weight") {
          const stonePrice = parseFloat(item.stone_price) || 0;
          const makingCharges = parseFloat(item.making_charges) || 0;
          const rateAmt = parseFloat(item.rate_amt) || 0;
          const hmCharges = parseFloat(item.hm_charges) || 0;
          itemTotal = stonePrice + makingCharges + rateAmt + hmCharges;
        } else {
          const pieceCost = parseFloat(item.pieace_cost) || 0;
          const qty = parseFloat(item.qty) || 0;
          itemTotal = pieceCost * qty;
        }

        // Calculate item's share of the total amount
        const itemShare = itemTotal / totalAmountWithoutDiscounts;

        // Calculate item's net amount based on the calculatedNetAmount
        const itemNet = calculatedNetAmount * itemShare;

        // Calculate taxable amount after subtracting festival discount
        const itemTaxableBeforeFestival = itemNet / (1 + itemTaxPercent / 100);
        const itemTaxable = Math.max(
          0,
          itemTaxableBeforeFestival - itemFestivalDiscount
        );
        const itemTax = itemTaxable * (itemTaxPercent / 100);

        totalTaxableAmount += itemTaxable;
        totalTaxAmount += itemTax;
      });

      // Now calculate the required discount amount (excluding festival discount)
      const totalAfterFestivalDiscount =
        totalAmountWithoutDiscounts - festivalDiscountAmt;
      const requiredDiscountAmount =
        totalAfterFestivalDiscount - totalTaxableAmount;

      // Calculate discount percentage based on making charges and piece cost only
      const discountBase = totalMakingCharges + totalPieceCost;
      const discountPercent = discountBase
        ? (requiredDiscountAmount * 100) / discountBase
        : 0;

      const roundedDiscount = parseFloat(discountPercent.toFixed(2));

      if (roundedDiscount !== discount) {
        setDiscount(roundedDiscount);
        localStorage.setItem(`discount`, roundedDiscount.toString());
        applyDiscountToRepairDetails(roundedDiscount, true); // Pass true to preserve festival discount
      }

      setManualNetAmount(calculatedNetAmount);
    }
  };

  // Function to handle manual net payable amount changes with reverse calculation
  // New flow: changing Net Payable adjusts Net Amount first, then recalculates discount
  const handleManualNetPayableChange = (newNetPayableAmount) => {
    // Calculate totals and validate against current expected payable
    const totalDeductions =
      oldItemsAmount + schemeAmount + salesAmountToPass + totalAdvanceAmount;
    const expectedNetPayable = netAmount - totalDeductions;

    // Prevent entering a value greater than expected payable
    const tolerance = 1; // allow rounding difference
    if (newNetPayableAmount - expectedNetPayable > tolerance) {
      // Debug logging with full breakdown
      // eslint-disable-next-line no-console
      console.debug(
        "[SalesForm.handleManualNetPayableChange] Validation failed",
        {
          enteredNetPayable: newNetPayableAmount,
          expectedNetPayable,
          netAmount,
          deductions: {
            oldItemsAmount,
            schemeAmount,
            totalSalesReturnAmount,
            totalAdvanceAmount,
          },
        }
      );

      alert(
        `Net Payable (${newNetPayableAmount}) cannot exceed calculated (${expectedNetPayable}).\n` +
          `netAmount=${netAmount}, deductions: oldItems=${oldItemsAmount}, scheme=${schemeAmount}, salesReturn=${totalSalesReturnAmount}, advance=${totalAdvanceAmount}`
      );
      return false; // Validation failed
    }

    // Derive the target Net Amount from the desired Net Payable and current deductions
    const targetNetAmount = newNetPayableAmount + totalDeductions;

    // Calculate the discount needed to achieve the target net amount
    // We need to work backwards from the target net amount to find the discount percentage
    const totalAmountBeforeDiscount = repairDetails.reduce((sum, item) => {
      const rateAmt = parseFloat(item.rate_amt) || 0;
      const stonePrice = parseFloat(item.stone_price) || 0;
      const makingCharges = parseFloat(item.making_charges) || 0;
      const hmCharges = parseFloat(item.hm_charges) || 0;
      return sum + rateAmt + stonePrice + makingCharges + hmCharges;
    }, 0);

    // Calculate the discount amount needed
    const currentNetAmount = repairDetails.reduce((sum, item) => {
      return sum + parseFloat(item.total_price) || 0;
    }, 0);

    const discountAmount = currentNetAmount - targetNetAmount;

    // Calculate discount percentage
    const discountPercentage =
      totalAmountBeforeDiscount > 0
        ? (discountAmount / totalAmountBeforeDiscount) * 100
        : 0;

    console.log("SalesForm Discount Calculation:", {
      targetNetAmount,
      currentNetAmount,
      totalAmountBeforeDiscount,
      discountAmount,
      discountPercentage,
      newNetPayableAmount,
      totalDeductions,
    });

    // Apply the discount
    if (discountPercentage !== discount) {
      setDiscount(discountPercentage);
      localStorage.setItem(`discount`, discountPercentage.toString());
      applyDiscountAmountToRepairDetails(discountAmount);
    }

    // Enter manual net mode and set the manual Net Amount
    setManualNetAmount(targetNetAmount);
    setIsManualNetMode(true);

    // Persist the manually chosen net payable so payableAmount aligns immediately in UI
    setSavedNetPayableAmount(newNetPayableAmount);
    setIsManualNetPayableSet(true);

    return true; // Success
  };

  // Function to apply discount as a direct amount
  const applyDiscountAmountDirectly = (discountAmount) => {
    const storedRepairDetails =
      JSON.parse(localStorage.getItem(`repairDetails`)) || [];

    if (storedRepairDetails.length === 0) return;

    // Calculate total base for proportional distribution
    let totalBase = 0;
    storedRepairDetails.forEach((item) => {
      const pricing = item.pricing;
      if (pricing === "By Weight") {
        const makingCharges = parseFloat(item.making_charges) || 0;
        totalBase += makingCharges;
      } else {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 1;
        totalBase += pieceCost * qty;
      }
    });

    // Apply discount proportionally to each item
    const updatedRepairDetails = storedRepairDetails.map((item) => {
      const pricing = item.pricing;
      const taxPercent = parseFloat(item.tax_percent) || 1;
      const festivalDiscount = parseFloat(item.festival_discount) || 0;

      let itemBase = 0;
      if (pricing === "By Weight") {
        const makingCharges = parseFloat(item.making_charges) || 0;
        itemBase = makingCharges;
      } else {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 1;
        itemBase = pieceCost * qty;
      }

      // Calculate this item's share of the discount
      const itemDiscountShare =
        totalBase > 0 ? (itemBase / totalBase) * discountAmount : 0;
      const currentDiscount = parseFloat(item.disscount) || 0;
      const newDiscount = currentDiscount + itemDiscountShare;

      if (pricing === "By fixed") {
        const pieceCost = parseFloat(item.pieace_cost) || 0;
        const qty = parseFloat(item.qty) || 1;
        const pieceTaxableAmt = pieceCost * qty;

        const originalPieceTaxableAmt = item.original_piece_taxable_amt
          ? parseFloat(item.original_piece_taxable_amt)
          : pieceTaxableAmt;

        const updatedPieceTaxableAmt =
          originalPieceTaxableAmt - newDiscount - festivalDiscount;
        const taxAmt = (taxPercent * updatedPieceTaxableAmt) / 100;
        const totalPrice = updatedPieceTaxableAmt + taxAmt;

        return {
          ...item,
          original_piece_taxable_amt: originalPieceTaxableAmt.toFixed(2),
          disscount: newDiscount.toFixed(2),
          piece_taxable_amt: updatedPieceTaxableAmt.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: totalPrice.toFixed(2),
        };
      } else {
        const rateAmt = parseFloat(item.rate_amt) || 0;
        const stonePrice = parseFloat(item.stone_price) || 0;
        const makingCharges = parseFloat(item.making_charges) || 0;
        const hmCharges = parseFloat(item.hm_charges) || 0;

        const previousTotalPrice = parseFloat(item.total_price) || 0;
        const originalTotalPrice = item.original_total_price
          ? parseFloat(item.original_total_price)
          : previousTotalPrice;

        const totalBeforeTax =
          rateAmt +
          stonePrice +
          makingCharges +
          hmCharges -
          newDiscount -
          festivalDiscount;
        const taxAmt = (totalBeforeTax * taxPercent) / 100;
        const updatedTotalPrice = totalBeforeTax + taxAmt;

        return {
          ...item,
          original_total_price: originalTotalPrice.toFixed(2),
          disscount: newDiscount.toFixed(2),
          tax_amt: taxAmt.toFixed(2),
          total_price: updatedTotalPrice.toFixed(2),
        };
      }
    });

    setRepairDetails(updatedRepairDetails);
    localStorage.setItem(
      `repairDetails`,
      JSON.stringify(updatedRepairDetails)
    );

    // Update the discount state to show the total discount amount
    setDiscount(discountAmount);
  };

  // Update the payableAmount calculation
  // When deductions are present, always use calculated amount to ensure proper deductions
  const hasDeductions =
    oldItemsAmount > 0 ||
    schemeAmount > 0 ||
    totalSalesReturnAmount > 0 ||
    totalAdvanceAmount > 0;

  // Calculate the base net payable (before manual adjustments)
  const calculatedNetPayable =
    netAmount -
    (schemeAmount + oldItemsAmount + salesAmountToPass + totalAdvanceAmount);
  const baseNetPayable = calculatedNetPayable; // Allow negative values for credit situations

  // Determine the final payable amount
  let payableAmount;
  if (isManualNetPayableSet) {
    // User has manually set the net payable amount
    payableAmount = savedNetPayableAmount;
  } else if (hasDeductions) {
    // Auto-calculate when deductions are present
    payableAmount = baseNetPayable;
  } else if (isManualNetMode) {
    // Manual net mode without deductions
    payableAmount = manualNetPayAmount;
  } else {
    // Default case
    payableAmount = netAmount;
  }

  const netPayableAmount = payableAmount;

  // const updatedOldItemsAmount = oldItemsAmount + salesNetAmount;
  const netPayAmount = netPayableAmount;

  const [paymentDetails, setPaymentDetails] = useState({
    cash_amount: "", // Fix to two decimal places
    card_amt: "",
    chq: "",
    chq_amt: 0,
    online: "",
    online_amt: 0,
  });
    useEffect(() => {
    console.log("Updated payment details:", paymentDetails);
  }, [paymentDetails]);

  // Save payment details to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(
      `paymentDetails`,
      JSON.stringify(paymentDetails)
    );
  }, [paymentDetails]);

  const clearData = () => {
    setOldSalesData([]);
    setSchemeSalesData([]);
    setRepairDetails([]);
    setPaymentDetails({
      cash_amount: 0,
      card_amt: 0,
      chq: "",
      chq_amt: 0,
      online: "",
      online_amt: 0,
    });
    setOldTableData([]); // Clear the oldTableData state
    setSchemeTableData([]);
    setDiscount(0);
    setSalesReturnData([]);
    setSelectedRows([]);
    setIsAllSelected(false);
    setReturnData({
      invoice_number: "",
    });
    localStorage.removeItem("oldSalesData");
    localStorage.removeItem("schemeSalesData");
    localStorage.removeItem(`repairDetails`);
    localStorage.removeItem(`paymentDetails`);
    localStorage.removeItem(`oldTableData`);
    localStorage.removeItem(`schemeTableData`);
    localStorage.removeItem(`discount`);
    localStorage.removeItem(`saleFormData`);
    localStorage.removeItem(`salesReturnData`);
    clearLocalStock(); // Clear local stock when clearing data
  };
  useEffect(() => {
    // Calculate the correct net payable amount with deductions
    const updatedNetPayableAmount = Math.round(
      netAmount -
        oldItemsAmount -
        schemeAmount -
        salesAmountToPass -
        totalAdvanceAmount
    );

    // Allow negative values when deductions exceed net amount (credit situation)
    const finalNetPayableAmount = updatedNetPayableAmount;

    // Only update savedNetPayableAmount if it hasn't been manually set
    // This allows users to manually edit the net payable amount
    if (
      !isManualNetPayableSet &&
      finalNetPayableAmount !== savedNetPayableAmount
    ) {
      setSavedNetPayableAmount(finalNetPayableAmount);
    }
  }, [
    netAmount,
    oldItemsAmount,
    schemeAmount,
    salesAmountToPass,
    totalAdvanceAmount,
    isManualNetPayableSet,
  ]);

  const handleSave = async (page = "sales") => {
    if (!formData.account_name || !formData.mobile) {
      alert("Please select the Customer or enter the Customer Mobile Number");
      mobileRef.current?.focus();
      return;
    }

    if (!repairDetails || repairDetails.length === 0) {
      alert("Please add at least one product before saving");
      return;
    }

    const balanceAmount = Math.round(
      savedNetPayableAmount -
        (parseFloat(paymentDetails.cash_amount || 0) +
          parseFloat(paymentDetails.card_amt || 0) +
          parseFloat(paymentDetails.chq_amt || 0) +
          parseFloat(paymentDetails.online_amt || 0))
    );

    const hasDelivered = repairDetails.some(
      (item) => item.sale_status === "Delivered"
    );



    try {
      setSaveLoading(true);
      // Fetch tags
      const tagResponse = await fetch(`${baseURL}/get/opening-tags-entry`);
      const tagResult = await tagResponse.json();
      const tagData = tagResult.result || [];

      // Check for sold items
      const soldItem = repairDetails.find((item) => {
        const matchedTag = tagData.find(
          (data) => data.PCode_BarCode === item.code && data.Status === "Sold"
        );
        return matchedTag !== undefined;
      });

      // Optional: re-enable if needed
      // if (soldItem) {
      //   alert(`Item with code "${soldItem.code}" is already sold out.`);
      //   return;
      // }

      // Handle invoice number
      const allItemsAreNew = repairDetails.every((item) => item.id === "");
      let updatedFormData = { ...formData };
      let isNewInvoice = false; // Track if we're creating a new invoice

      if (allItemsAreNew) {
        const response = await axios.get(`${baseURL}/lastInvoiceNumber`);
        const latestInvoiceNumber = response.data.lastInvoiceNumber;

        updatedFormData = {
          ...formData,
          invoice_number: latestInvoiceNumber,
        };

        setFormData(updatedFormData);
        isNewInvoice = true; // Mark as new invoice
      }

      // Prepare sales return items from selected rows
      const saleReturnItems = selectedRows.map((rowIndex) => {
        const detail = invoiceDetails[rowIndex];
        return {
          original_invoice_number:
            detail.invoice_number || returnData.invoice_number,
          product_name: detail.product_name || "",
          metal: detail.metal_type || detail.metal || "",
          purity: detail.purity || "",
          gross_weight: detail.gross_weight || "",
          stone_weight: detail.stone_weight || "",
          rate: detail.rate || "",
          rate_amt: detail.rate_amt || "",
          making_charges: detail.making_charges || "",
          stone_price: detail.stone_price || "",
          hm_charges: detail.hm_charges || "",
          disscount: detail.disscount || "",
          festival_discount: detail.festival_discount || "",
          tax_percent: detail.tax_percent || "",
          tax_amt: detail.tax_amt || "",
          total_price: detail.total_price || "",
          qty: detail.qty || 1,
          status: "Sale Returned",
          transaction_type: "sale_return",
          return_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
          remarks: "Customer returned due to size issue",
          category: detail.category || "",
          sub_category: detail.sub_category || "",
          design_name: detail.design_name || "",
          code: detail.code || "",
          customer_id: formData.customer_id || "",
          customer_name: formData.account_name || "",
        };
      });

      // Prepare payload - transform estimate-based sales to normal sales structure
      // For sales returns, ensure we always create new records without IDs
      const originalIds = repairDetails
        .filter((item) => item.id)
        .map((item) => item.id);
      console.log("repair details are", repairDetails);
      const dataToSave = {
        repairDetails: repairDetails.map((item) => {
          // Always ensure ID is empty for new records to force INSERT instead of UPDATE
          const cleanItem = { ...item};
          // Check if this is from an estimate (has estimate_number)
          const isFromEstimate =
            cleanItem.estimate_number && cleanItem.estimate_number !== "";

          if (isFromEstimate) {
            // Transform estimate-based sale to normal sale structure
            // Clear ID when creating a new invoice (converting estimate to sales)
            // Keep ID when editing existing sales invoice
            
            return {
              ...cleanItem,
              id: isNewInvoice ? "" : cleanItem.id, // Clear ID only for new invoices
              customer_id: updatedFormData.customer_id,
              mobile: updatedFormData.mobile,
              account_name: updatedFormData.account_name,
              email: updatedFormData.email,
              address1: updatedFormData.address1,
              address2: updatedFormData.address2,
              city: updatedFormData.city,
              pincode: updatedFormData.pincode,
              state: updatedFormData.state,
              state_code: updatedFormData.state_code,
              aadhar_card: updatedFormData.aadhar_card,
              gst_in: updatedFormData.gst_in,
              pan_card: updatedFormData.pan_card,
              terms: updatedFormData.terms,
              date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), // Simple date format
              invoice_number: updatedFormData.invoice_number,
              estimate_number: cleanItem.estimate_number, // Empty estimate number
              code: cleanItem.code,
              product_id: cleanItem.product_id || "",
              metal: cleanItem.metal_type,
              product_name: cleanItem.product_name,
              metal_type: cleanItem.metal_type,
              design_name: cleanItem.design_name || "",
              purity: cleanItem.purity,
              selling_purity: cleanItem.purity,
              printing_purity: cleanItem.purity,
              pricing: "By Weight",
              category: cleanItem.category,
              sub_category: cleanItem.sub_category,
              gross_weight: cleanItem.gross_weight,
              stone_weight: cleanItem.stone_weight,
              weight_bw: cleanItem.weight_bw,
              stone_price: cleanItem.stone_price,
              va_on: "Gross Weight",
              va_percent: cleanItem.va_percent,
              wastage_weight: cleanItem.wastage_weight,
              total_weight_av: cleanItem.total_weight_av,
              mc_on: "MC %",
              disscount_percentage: 0,
              disscount: cleanItem.disscount,
              mc_per_gram: cleanItem.mc_per_gram,
              making_charges: cleanItem.making_charges,
              rate: cleanItem.rate,
              pieace_cost: null,
              mrp_price: "",
              rate_amt: cleanItem.rate_amt,
              tax_percent: cleanItem.tax_percent,
              tax_amt: cleanItem.tax_amt,
              hm_charges: cleanItem.hm_charges,
              total_price: cleanItem.total_price,
              transaction_status: "Sales",
              qty: cleanItem.qty,
              opentag_id: "",
              product_image: null,
              imagePreview: cleanItem.imagePreview,
              remarks: cleanItem.remarks,
              sale_status: "Delivered",
              estimate_status: "Not Delivered",
              piece_taxable_amt: cleanItem.piece_taxable_amt,
              festival_discount: cleanItem.festival_discount,
              custom_purity: "",
              manual_price_update: false,
              order_number: "",
              receipts_amt: "",
              bal_after_receipts: "",
              rate_24k: cleanItem.rate,
              total_weight_aw: "",
              original_total_price: cleanItem.total_price,
              festival_discount_percentage: "0.00",
              festival_discount_on_rate: "0.00",
              cash_amount: paymentDetails.cash_amount || 0,
              card_amt: paymentDetails.card_amt || 0,
              chq_amt: paymentDetails.chq_amt || 0,
              online_amt: paymentDetails.online_amt || 0,
              sale_return_amt: salesAmountToPass || 0, // Use taxable-or-net based on month
            };
          } else {
            // Normal sale - keep existing structure but ensure ID is empty for new records
            return {
              ...cleanItem,
              invoice_number: updatedFormData.invoice_number,
              customer_id: updatedFormData.customer_id,
              mobile: updatedFormData.mobile,
              account_name: updatedFormData.account_name,
              email: updatedFormData.email,
              address1: updatedFormData.address1,
              address2: updatedFormData.address2,
              city: updatedFormData.city,
              pincode: updatedFormData.pincode,
              state: updatedFormData.state,
              state_code: updatedFormData.state_code,
              aadhar_card: updatedFormData.aadhar_card,
              gst_in: updatedFormData.gst_in,
              pan_card: updatedFormData.pan_card,
              terms: updatedFormData.terms,
              cash_amount: paymentDetails.cash_amount || 0,
              card_amt: paymentDetails.card_amt || 0,
              chq_amt: paymentDetails.chq_amt || 0,
              online_amt: paymentDetails.online_amt || 0,
              sale_return_amt: salesAmountToPass || 0, // Use taxable-or-net based on month
              is_sales_generated: cleanItem.is_sales_generated,
            };
          }
        }),
        oldItems: oldSalesData,
        memberSchemes: schemeSalesData,
        oldItemsAmount: oldItemsAmount || 0,
        schemeAmount: schemeAmount || 0,
        salesNetAmount: salesAmountToPass || 0,
        salesTaxableAmount: salesTaxableAmount || 0,
        roundOffAmount: roundOffAmountForSaveRepairsApiCall || 0,
        saleReturnItems: saleReturnItems, // Add sale return items to payload
      };
      console.log("data to save", dataToSave);
      if (location?.state?.from === "orders") {
        try {
          console.log("Sending IDs to mark-sales-generated:", originalIds);
          await axios.post(`${baseURL}/mark-sales-generated`, {
            ids: originalIds,
          });

          console.log("Sales status updated in DB");
        } catch (err) {
          console.error("Error updating sales status:", err);
        }
      }

      await axios.post(`${baseURL}/save-repair-details`, dataToSave);
      alert("Sales added successfully");
      try {
        const { data: repairItems } = await axios.get(
          `${baseURL}/get-repair-details/${updatedFormData.invoice_number}`
        );
        console.log(repairItems, "after saving data")

        if (repairItems && repairItems.repeatedData && repairItems.repeatedData.length > 0) {
          const estimateItems = repairItems.repeatedData.filter(
            (item) => item.estimate_number && item.estimate_number !== ""
          );

          if (estimateItems.length > 0) {
            await Promise.all(
              estimateItems.map((item) =>
                axios.put(
                  `${baseURL}/Estimate/link-sales/${item.estimate_number}`,
                  {
                    repair_details_id: item.id || null,
                    invoice_no: updatedFormData.invoice_number,
                  }
                )
              )
            );
            console.log("Linked estimates updated successfully");
          } else {
            console.log("No estimates to update for this invoice");
          }
        } else {
          console.log("No repair details found for this invoice");
        }
      } catch (err) {
        console.error("Error updating linked estimates:", err);
      }
      if (updatedFormData.invoice_number) {
        const encodedInvoiceNumber = encodeURIComponent(updatedFormData.invoice_number);
        window.open(`/pdf/sales/${encodedInvoiceNumber}`, "_blank", "noopener,noreferrer");
      }

      // Cleanup
      clearData();
      resetForm();

      // navigate("/salestable");
      // window.location.reload(); // Removed to prevent unexpected reloads
      await handleCheckout();
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Error saving data");
    } finally {
      setSaveLoading(false);
    }
  };

  const refreshSalesData = () => {
    setOldSalesData([]);
    setSchemeSalesData([]);
    setRepairDetails([]);
    setPaymentDetails({
      cash_amount: 0,
      card_amt: 0,
      chq: "",
      chq_amt: 0,
      online: "",
      online_amt: 0,
    });
    setOldTableData([]); // Clear the oldTableData state
    setSchemeTableData([]);
    setDiscount(0);
    setSalesReturnData([]);
    setSelectedRows([]);
    setIsAllSelected(false);
    setReturnData({
      invoice_number: "",
    });
    localStorage.removeItem("oldSalesData");
    localStorage.removeItem("schemeSalesData");
    localStorage.removeItem(`repairDetails`);
    localStorage.removeItem(`paymentDetails`);
    localStorage.removeItem(`oldTableData`);
    localStorage.removeItem(`schemeTableData`);
    localStorage.removeItem(`discount`);
    localStorage.removeItem(`salesReturnData`);

    window.location.reload();
  };

  const mobileRef = useRef(null); // Reference to Mobile input field in CustomerDetails

  useEffect(() => {
    if (formData.code) {
      mobileRef.current?.focus(); // Move focus to Mobile when BarCode is entered
    }
  }, [formData.code]);

  // Apply calculations
  // useCalculations(formData, setFormData, offers,  isManualTotalPriceChange,
  //   setIsManualTotalPriceChange, isTotalPriceCleared);

  useEffect(() => {}, [
    totalAmount,
    taxableAmount,
    taxAmount,
    netAmount,
    discountAmt,
    festivalDiscountAmt,
  ]);
  const [fromOrd, setIsFromOrd]=useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Only run if coming from Orders and orderDetails is present
      if (
        location.state?.from === "orders" &&
        location.state?.orderDetails &&
        location.state.orderDetails.length > 0
      ) {
        const item = location.state.orderDetails[0];
        console.log("order details:", location.state.orderDetails[0]);
        setRepairDetails(location.state.orderDetails);
        setIsFromOrd(true);
        setOldItemsAmount(item.old_exchange_amt);
        console.log("old items is", oldItemsAmount);

        setFormData((prev) => ({
          ...prev,
          // Customer fields
          customer_id: item.customer_id ?? "",
          account_name: item.account_name ?? "",
          mobile: item.mobile ?? "",
          email: item.email ?? "",
          address1: item.address1 ?? "",
          address2: item.address2 ?? "",
          city: item.city ?? "",
          pincode: item.pincode ?? "",
          state: item.state ?? "",
          state_code: item.state_code ?? "",
          aadhar_card: item.aadhar_card ?? "",
          gst_in: item.gst_in ?? "",
          pan_card: item.pan_card ?? "",
          // Product fields
          category: item.category ?? "",
          code: item.code ?? "",
          metal_type: item.metal_type ?? "",
          product_name: item.product_name ?? "",
          selling_purity:item.selling_purity??"",
          design_name: item.design_name ?? "",
          pricing: item.pricing ?? "",
          purity: item.purity ?? "",
          gross_weight: item.gross_weight ?? "",
          stone_weight: item.stone_weight ?? "",
          stone_price: item.stone_price ?? "",
          weight_bw: item.weight_bw ?? "",
          va_on: item.va_on ?? "",
          va_percent: item.va_percent ?? "",
          wastage_weight: item.wastage_weight ?? "",
          total_weight_av: item.total_weight_av ?? "",
          size: item.size ?? "",
          rate: item.rate ?? "",
          rate_amt: item.rate_amt ?? "",
          mc_on: item.mc_on ?? "",
          mc_per_gram: item.mc_per_gram ?? "",
          making_charges: item.making_charges ?? "",
          hm_charges: item.hm_charges ?? "",
          tax_percent: item.tax_percent ?? "",
          tax_amt: item.tax_amt ?? "",
          total_price: item.total_price ?? "",
          remarks: item.remarks ?? "",
          qty: item.qty ?? "",
          imagePreview: item.imagePreview ?? "",
          client_visibility: "Internal Facing",
        }));

        // fetch old items for this order
        try {
          setOldSalesData([]);
          setOldTableData([]);
          localStorage.setItem(`oldTableData`, JSON.stringify([]));
          setIsManualNetPayableSet(false);
          const oldItemsResponse = await axios.get(
            `${baseURL}/get/olditems/${item.order_number}`
          );

          if (oldItemsResponse.data && oldItemsResponse.data.length > 0) {
            const mappedOldItems = oldItemsResponse.data.map((item) => ({
              product: item.product || "",
              metal: item.metal || "",
              purity: item.purity || "",
              purityPercentage: item.purity_percentage || "",
              hsn_code: item.hsn_code || "",
              gross: parseFloat(item.gross) || 0,
              dust: parseFloat(item.dust) || 0,
              ml_percent: parseFloat(item.ml_percent) || 0,
              net_wt: parseFloat(item.net_wt) || 0,
              remarks: item.remarks || "",
              rate: parseFloat(item.rate) || 0,
              total_amount: parseFloat(item.total_amount) || 0,
              total_old_amount: oldItemsResponse.data.reduce(
                (sum, i) => sum + parseFloat(i.total_amount || 0),
                0
              ),
            }));

            setOldSalesData(mappedOldItems);
            setOldTableData(mappedOldItems);
            localStorage.setItem(
              `oldTableData`,
              JSON.stringify(mappedOldItems)
            );

            if (mappedOldItems.length > 0) {
              setIsManualNetPayableSet(false);
            }
          } else {
            setOldSalesData([]);
            setOldTableData([]);
            localStorage.setItem(`oldTableData`, JSON.stringify([]));
            setIsManualNetPayableSet(false);
          }
        } catch (error) {
          console.error("Error fetching old items:", error);
          setOldSalesData([]);
          setOldTableData([]);
          setIsManualNetPayableSet(false);
        }
      }
    };

    fetchData();
  }, [location.state]);

  // Inside SalesForm.js, just before the return (
  console.log("net amount is", netAmount);
  console.log("net payable amount is", netPayableAmount);

  return (
    <div className="main-container">
      <Container className="sales-form-container">
        <Form>
          {/* <h3 style={{ marginTop: '-45px', marginBottom: '10px', textAlign: 'left', color: '#a36e29' }}>
            Sales
          </h3> */}
          <div className="sales-form" style={{ marginTop: "-40px" }}>
            <div className="sales-form-left">
              <CustomerDetails
                formData={formData}
                setFormData={setFormData}
                handleCustomerChange={handleCustomerChange}
                handleAddCustomer={handleAddCustomer}
                customers={customers}
                setSelectedMobile={setSelectedMobile} // Pass the setSelectedMobile function here
                mobileRef={mobileRef}
                isEstimatePage={isEstimatePage}
              />
            </div>
            <div className="sales-form-right">
              <InvoiceDetails
                formData={formData}
                setFormData={setFormData}
                currentInvoiceIndex={currentInvoiceIndex}
                totalInvoices={sortedInvoices.length}
                onPrev={navigateToPreviousInvoice}
                onNext={navigateToNextInvoice}
                onFirst={navigateToFirstInvoice}
                onLast={navigateToLastInvoice}
              />
            </div>
          </div>
          <div
            className="sales-form-section"
            style={{ marginTop: "-20px", marginBottom: "5px" }}
          >
            <ProductDetails
              fromOrd={fromOrd}
              EstimateNo={EstimateNo}
              formData={formData}
              setFormData={setFormData}
              handleChange={handleChange}
              handleBarcodeChange={handleBarcodeChange}
              handleProductChange={handleProductChange}
              handleProductNameChange={handleProductNameChange}
              handleMetalTypeChange={handleMetalTypeChange}
              handleDesignNameChange={handleDesignNameChange}
              handleAdd={handleAdd}
              products={products}
              data={data}
              uniqueProducts={uniqueProducts}
              categoryOptions={categoryOptions}
              subcategoryOptions={subcategoryOptions}
              purityOptions={purityOptions}
              designOptions={designOptions}
              metaltypeOptions={metaltypeOptions}
              filteredMetalTypes={filteredMetalTypes}
              filteredPurityOptions={filteredPurityOptions}
              filteredDesignOptions={filteredDesignOptions}
              isBarcodeSelected={isBarcodeSelected}
              isQtyEditable={isQtyEditable}
              handleUpdate={handleUpdate}
              isEditing={editIndex !== null}
              handleImageChange={handleImageChange}
              image={image}
              fileInputRef={fileInputRef}
              clearImage={clearImage}
              captureImage={captureImage}
              setShowWebcam={setShowWebcam}
              showWebcam={showWebcam}
              webcamRef={webcamRef}
              setShowOptions={setShowOptions}
              showOptions={showOptions}
              estimate={estimate}
              selectedEstimate={selectedEstimate}
              handleEstimateChange={handleEstimateChange}
              refreshSalesData={refreshSalesData}
              fetchCategory={fetchCategory}
              fetchSubCategory={fetchSubCategory}
              taxableAmount={taxableAmount}
              offers={offers}
              isTotalPriceCleared={isTotalPriceCleared}
              setIsTotalPriceCleared={setIsTotalPriceCleared}
              isManualTotalPriceChange={isManualTotalPriceChange}
              setIsManualTotalPriceChange={setIsManualTotalPriceChange}
              manualTotalPriceRef={manualTotalPriceRef}
              handleOrderChange={handleOrderChange}
              selectedOrder={selectedOrder}
              orderData={orderData}
            />
          </div>

          <div className="sales-form-section">
            <ProductTable
              highlightDelivered={highlightDelivered}
              repairDetails={repairDetails}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>
          <div className="sales-form">
            <div className="sales-form-third">
              <SalesFormSection
                key={resetActiveForm ? "reset" : "normal"}
                metal={metal}
                setMetal={setMetal}
                setOldSalesData={setOldSalesData}
                oldTableData={oldTableData}
                setOldTableData={setOldTableData}
                setSchemeSalesData={setSchemeSalesData}
                schemeTableData={schemeTableData}
                setSchemeTableData={setSchemeTableData}
                filteredInvoices={filteredInvoices}
                setFilteredInvoices={setFilteredInvoices}
                uniqueInvoice={uniqueInvoice}
                setUniqueInvoice={setUniqueInvoice}
                invoiceDetails={invoiceDetails}
                setInvoiceDetails={setInvoiceDetails}
                handleInvoiceChange={handleInvoiceChange}
                returnData={returnData}
                setReturnData={setReturnData}
                selectedMobile={formData.mobile}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                isAllSelected={isAllSelected}
                setIsAllSelected={setIsAllSelected}
                handleCheckboxChange={handleCheckboxChange}
                handleSelectAllChange={handleSelectAllChange}
                salesTaxableAmount={salesTaxableAmount}
                salesTaxAmount={salesTaxAmount}
                salesNetAmount={salesNetAmount}
                repairDetails={repairDetails}
                resetSaleReturnForm={resetSaleReturnForm}
                handleCheckout={handleCheckout}
                setRepairDetails={setRepairDetails}
                formData={formData}
                orderData={orderData}
                handleCloseModal={handleCloseModal}
                handleViewDetails={handleViewDetails}
                selectedOrder={selectedOrder}
                handleOrderCheckboxChange={handleOrderCheckboxChange}
                showModal={showModal}
                orderDetails={orderDetails}
                loading={loading}
                formatDate={formatDate}
                repairs={repairs}
                handleRepairCheckboxChange={handleRepairCheckboxChange}
                salesReturnData={salesReturnData}
                estimateNumber={formData.invoice_number}
                resetActiveForm={resetActiveForm}
              />
            </div>
            <div className="sales-form-fourth">
              <PaymentDetails
                loadInvoiceData={loadInvoiceData}
                setInvoiceDataLoaded={setInvoiceDataLoaded}
                orderDetails={orderDetails}
                paymentDetails={paymentDetails}
                setPaymentDetails={setPaymentDetails}
                handleSave={handleSave}
                saving={saveLoading}
                handleBack={handleBack}
                totalPrice={totalPrice}
                repairDetails={repairDetails}
                setRepairDetails={setRepairDetails}
                taxableAmount={taxableAmount}
                discountAmt={discountAmt}
                totalAmount={totalAmount}
                taxAmount={taxAmount}
                netAmount={netAmount}
                oldItemsAmount={oldItemsAmount}
                schemeAmount={schemeAmount}
                netPayableAmount={netPayableAmount}
                salesNetAmount={salesNetAmount}
                salesAmountToPass={salesAmountToPass}
                salesTaxableAmount={salesTaxableAmount}
                savedNetPayableAmount={savedNetPayableAmount}
                setSavedNetPayableAmount={setSavedNetPayableAmount}
                setIsManualNetPayableSet={setIsManualNetPayableSet}
                netPayAmount={netPayAmount}
                oldSalesData={oldSalesData}
                schemeSalesData={schemeSalesData}
                discount={discount}
                handleDiscountChange={handleDiscountChange}
                refreshSalesData={refreshSalesData}
                setOffers={setOffers}
                offers={offers}
                loading={loading}
                festivalShowModal={festivalShowModal}
                handleFestivalShowModal={handleFestivalShowModal}
                handleFestivalCloseModal={handleFestivalCloseModal}
                appliedOffers={appliedOffers}
                setAppliedOffers={setAppliedOffers}
                festivalDiscountAmt={festivalDiscountAmt}
                manualNetAmount={manualNetAmount}
                setManualNetAmount={setManualNetAmount}
                handleManualNetAmountChange={handleManualNetAmountChange}
                isManualNetMode={isManualNetMode}
                setIsManualNetMode={setIsManualNetMode}
                handleManualNetPayAmountChange={handleManualNetPayAmountChange}
                manualNetPayAmount={manualNetPayAmount}
                handleManualNetPayableChange={handleManualNetPayableChange}
                handleApply={handleApply}
                isAnyOfferApplied={isAnyOfferApplied}
                applyDiscountAmountToRepairDetails={
                  applyDiscountAmountToRepairDetails
                }
                setRoundOffAmountForSaveRepairsApiCall={
                  setRoundOffAmountForSaveRepairsApiCall
                }
                invoice_number={formData.invoice_number}
                selectedRows={selectedRows}
                setNetPayableEditable={setNetPayableEditableRef}
                setResetActiveForm={setResetActiveForm}
                totalAdvanceAmount={totalAdvanceAmount}
              />
            </div>
          </div>
          {showPDFDownload && (
            <PDFDownloadLink
              document={
                <PDFLayout
                  formData={formData}
                  repairDetails={repairDetails}
                  paymentDetails={paymentDetails}
                />
              }
              fileName={`invoice-${formData.invoice_number}.pdf`}
            >
              {({ blob, url, loading, error }) =>
                loading ? "Generating PDF..." : "Download Invoice PDF"
              }
            </PDFDownloadLink>
          )}
        </Form>
      </Container>
    </div>
  );
};

export default SalesForm;
